import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import {
  adminListUsers, adminUpdateFee, adminStats, adminListPayouts, adminUpdatePayout,
} from '../api/payments';
import { adminGetSettings, adminUpdateSettings, adminPendingPosts, adminSetPostApproval,
  adminSetUserStatus, adminFinance, adminActiveLives, adminSmtpTest, adminSmsTest, adminLiveBroadcastPreview,
} from '../api/admin_phase6';
import { adminPauseUser, adminResumeUser, adminDeleteUser, adminStandbyUser, adminReleaseStandby } from '../api/account';
import { adminSuspendMonetization, adminResumeMonetization, adminSetUserRole,
  adminListEmailLogs, adminEnrichEmailLog,
} from '../api/payouts';
import {
  adminListRefunds, adminApproveRefund, adminDeclineRefund, adminReversePurchase,
} from '../api/shop';
import { mediaUrl } from '../api/posts';
import api from '../api/client';
import { fmtCredits } from '../lib/currency';
import AccountingOrdersPanel from '../components/AccountingOrdersPanel';
import AdminOverviewPanel from '../components/AdminOverviewPanel';
import OnlineNowCard from '../components/admin/OnlineNowCard';
import AdminHelpCenterPanel from '../components/AdminHelpCenterPanel';
import MaintenancePanel from '../components/MaintenancePanel';
import ContentModerationPanel from '../components/ContentModerationPanel';
import AdminReportsPanel from '../components/AdminReportsPanel';
import CreatorBillingProfileDialog from '../components/admin/CreatorBillingProfileDialog';
import AdminWalletAdjustDialog from '../components/admin/AdminWalletAdjustDialog';
import AdminUserCardPreviewDialog from '../components/admin/AdminUserCardPreviewDialog';
import AdminMediaPerformancePanel from '../components/admin/AdminMediaPerformancePanel';
import AdminCreatorFiscalPanel from '../components/admin/AdminCreatorFiscalPanel';
import AdminDisputesPanel from '../components/admin/AdminDisputesPanel';
import AdminTeamPanel from './admin/AdminTeamPanel';
import PostPreviewModal from '../components/PostPreviewModal';
import AdminLiveRefundsPanel from '../components/admin/AdminLiveRefundsPanel';
import AdminWhatsAppPanel from './admin/AdminWhatsAppPanel';
import AdminCampaignLinksPanel from './admin/AdminCampaignLinksPanel';
import AdminAperitivoPanel from './admin/AdminAperitivoPanel';
import AdminCategoriesPanel from '../components/admin/AdminCategoriesPanel';
import AdminAppleCleanupPanel from '../components/admin/AdminAppleCleanupPanel';
import StripeReadinessCard from '../components/StripeReadinessCard';
import MetaDiagnosticCard from '../components/admin/MetaDiagnosticCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useToast } from '../hooks/use-toast';
import {
  Users, FileVideo, BadgePercent, DollarSign, ArrowDownToLine, ShieldCheck, Pencil,
  Check, X, Settings as SettingsIcon, Wallet, Radio, KeyRound, FileCheck2,
  AlertTriangle, ChevronRight, Mail, Database, BarChart3, Send, PauseCircle, Play, Trash2, Smartphone, EyeOff, Eye, IdCard,
  Crown, Ban, RefreshCcw, Undo2, Stethoscope, Loader2, Save, Lock, Shield, Landmark, Megaphone, Martini, CalendarDays, Tags, ShieldAlert,
  Coins, MessageCircle, Flag, Search, Plus,
} from 'lucide-react';


/** Phase 24.E.2 — LiveKit health check. Posts to /api/admin/settings/livekit-test
 *  and renders each step's ok/error so admins can pinpoint "invalid API key"
 *  and other LiveKit Cloud handshake issues without SSHing into the server.
 */
const LiveKitDiagnostics = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  const run = async () => {
    setLoading(true); setResult(null); setErr('');
    try {
      const token = localStorage.getItem('ig_token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/livekit-test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
      setResult(j);
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="rounded-md border border-border bg-background/40 p-3" data-testid="livekit-diagnostics">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold">Health check</p>
          <p className="text-[11px] text-muted-foreground">Esegue mint JWT + create_room + WebSocket handshake reali contro LiveKit Cloud per individuare la causa esatta di errori tipo &quot;invalid API key&quot;.</p>
        </div>
        <Button
          data-testid="livekit-test-btn"
          size="sm"
          variant="outline"
          onClick={run}
          disabled={loading}
        >
          {loading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Stethoscope className="mr-1 h-4 w-4" />}
          {loading ? 'Testing…' : 'Test LiveKit'}
        </Button>
      </div>
      {err && (
        <p className="mt-2 rounded bg-red-50 px-2 py-1 text-[11px] text-red-700" data-testid="livekit-test-network-error">
          ❌ {err}
        </p>
      )}
      {result && (
        <div className="mt-2 space-y-1" data-testid="livekit-test-result">
          <p className={`text-xs font-semibold ${result.ok ? 'text-emerald-700' : 'text-red-700'}`}>
            {result.ok ? '✅ Tutti i controlli passati — LiveKit è configurato correttamente.' : '❌ Trovato un problema nella catena LiveKit.'}
          </p>
          <ul className="space-y-1">
            {result.steps.map((s) => (
              <li key={s.step} className={`rounded border px-2 py-1 text-[11px] ${s.ok ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`} data-testid={`livekit-step-${s.step}`}>
                <span className="font-mono">{s.ok ? '✅' : '❌'} {s.step}</span>
                {s.error && <span className="ml-2 text-red-700">— {s.error}</span>}
                {s.hint && <p className="mt-0.5 italic text-amber-700">💡 {s.hint}</p>}
                {!s.error && !s.hint && (
                  <span className="ml-2 text-muted-foreground">
                    {Object.entries(s).filter(([k]) => !['step','ok','error','hint'].includes(k))
                      .map(([k,v]) => `${k}=${typeof v==='object'?JSON.stringify(v):String(v)}`)
                      .join(' · ')}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/** Phase 27.1 — Wasabi S3 connection diagnostic.
 *  POSTs to /api/admin/settings/wasabi-test using the CURRENT FORM values
 *  as override (so the admin can validate a new bucket/region/endpoint
 *  BEFORE clicking "Save settings"). Renders the 8 ordered steps with
 *  precise error codes (HeadBucket 403 vs InvalidAccessKeyId vs PermanentRedirect)
 *  so the misconfig is surgical.
 */
const WasabiConnectionTester = ({ form }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  const run = async () => {
    setLoading(true); setResult(null); setErr('');
    try {
      const token = localStorage.getItem('ig_token');
      // Build override body from CURRENT form values. Empty fields
      // fall back to saved settings on the backend.
      const override = {
        access_key: form.wasabi_access_key || null,
        secret_key: form.wasabi_secret_key || null,
        bucket_name: form.wasabi_bucket_name || null,
        region: form.wasabi_region || null,
        endpoint: form.wasabi_endpoint || null,
        public_base_url: form.wasabi_public_base_url || null,
      };
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/wasabi-test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(override),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
      setResult(j);
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-2 rounded-md border border-border bg-background/40 p-3" data-testid="wasabi-diagnostics">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold">Test connessione Wasabi</p>
          <p className="text-[11px] text-muted-foreground">
            Esegue 8 controlli reali contro Wasabi (DNS → HeadBucket → PutObject → ListObjectsV2 → GetObject → signed URL → DeleteObject) usando i valori del form (anche se non hai ancora salvato).
          </p>
        </div>
        <Button
          data-testid="wasabi-test-btn"
          size="sm"
          variant="outline"
          onClick={run}
          disabled={loading}
        >
          {loading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Stethoscope className="mr-1 h-4 w-4" />}
          {loading ? 'Testing…' : 'Test Wasabi'}
        </Button>
      </div>

      {err && (
        <p className="mt-2 rounded bg-red-50 px-2 py-1 text-[11px] text-red-700" data-testid="wasabi-test-network-error">
          ❌ Errore di rete: {err}
        </p>
      )}

      {result && (
        <div className="mt-3 space-y-2" data-testid="wasabi-test-result">
          <p className={`text-sm font-semibold ${result.ok ? 'text-emerald-700' : 'text-red-700'}`}>
            {result.ok ? '✅ Wasabi è configurato correttamente — tutti i 9 step passano.' : `❌ ${result.summary}`}
          </p>
          <div className="rounded bg-background/60 p-2 text-[11px]">
            <p className="mb-1 font-semibold">Effective config (priority: <code>ENV</code> &gt; <code>DB</code> &gt; <code>form override</code>):</p>
            <pre className="overflow-x-auto whitespace-pre font-mono text-[10px]">
{`access_key:      ${result.effective_config.access_key_masked.padEnd(48)}[from: ${result.effective_config.sources?.access_key || '?'}]
secret_key:      ${result.effective_config.secret_key_masked.padEnd(48)}[from: ${result.effective_config.sources?.secret_key || '?'}]
bucket:          ${result.effective_config.bucket.padEnd(48)}[from: ${result.effective_config.sources?.bucket || '?'}]
region:          ${result.effective_config.region.padEnd(48)}[from: ${result.effective_config.sources?.region || '?'}]
endpoint:        ${result.effective_config.endpoint.padEnd(48)}[from: ${result.effective_config.sources?.endpoint || '?'}]
public_base_url: ${(result.effective_config.public_base_url || '(none)').padEnd(48)}[from: ${result.effective_config.sources?.public_base_url || '?'}]`}
            </pre>
            <p className="mt-2 text-[10px] text-muted-foreground">
              <strong>env</strong> = Emergent Secrets (canonico, sopravvive ai redeploy) ·{' '}
              <strong>db</strong> = Admin form salvato in <code>app_settings</code> ·{' '}
              <strong>form</strong> = override temporaneo per Test (NON persistito)
            </p>
          </div>
          {result.received_override && (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="wasabi-debug-trace">
              <div className="rounded bg-blue-50 p-2 text-[10px]">
                <p className="mb-1 font-semibold text-blue-900">📥 Override ricevuto dal frontend:</p>
                <pre className="whitespace-pre font-mono">
{`access_key_set:  ${result.received_override.access_key_set}
secret_key_set:  ${result.received_override.secret_key_set}
bucket_name:     ${JSON.stringify(result.received_override.bucket_name)}
region:          ${JSON.stringify(result.received_override.region)}
endpoint:        ${JSON.stringify(result.received_override.endpoint)}
public_base_url: ${JSON.stringify(result.received_override.public_base_url)}`}
                </pre>
              </div>
              <div className="rounded bg-neutral-100 p-2 text-[10px]">
                <p className="mb-1 font-semibold text-neutral-900">💾 Valori attualmente salvati in DB:</p>
                <pre className="whitespace-pre font-mono">
{`bucket_name:     ${JSON.stringify(result.saved_snapshot?.bucket_name)}
region:          ${JSON.stringify(result.saved_snapshot?.region)}
endpoint:        ${JSON.stringify(result.saved_snapshot?.endpoint)}
public_base_url: ${JSON.stringify(result.saved_snapshot?.public_base_url)}`}
                </pre>
              </div>
            </div>
          )}
          <ul className="space-y-1">
            {result.steps.map((s, idx) => (
              <li
                key={idx}
                className={`rounded border px-2 py-1.5 text-[11px] ${s.ok ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}
                data-testid={`wasabi-step-${idx}`}
              >
                <p className="font-mono font-semibold">
                  {s.ok ? '✅' : '❌'} {s.step}
                </p>
                {s.detail && <p className="mt-0.5 text-muted-foreground">{s.detail}</p>}
                {s.error && (
                  <div className="mt-1 rounded bg-red-100 p-1.5 font-mono text-[10px] text-red-900">
                    {Object.entries(s.error).map(([k, v]) => (
                      <p key={k}><strong>{k}:</strong> {String(v)}</p>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};


/** Phase 25 — Clean Test Data button.
 *  Calls POST /api/admin/cleanup/test-data which cascade-deletes every user
 *  marked `is_test=True` and all their related rows. Renders confirmation
 *  dialog, spinner, and a detailed report.  After success it broadcasts
 *  `window.dispatchEvent(new Event('nymia:data-changed'))` so Home/Explore/
 *  Lives pages that listen to it auto-refresh.
 */
const CleanTestDataPanel = () => {
  const { toast } = useToast();
  const [confirming, setConfirming] = React.useState(false);
  const [running, setRunning] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');

  const run = async () => {
    setRunning(true);
    setError('');
    setResult(null);
    setConfirming(false);
    try {
      const tok = localStorage.getItem('ig_token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cleanup/test-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tok}`,
        },
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
      setResult(j);
      toast({
        title: 'Cleanup completato',
        description: `${j.test_users_deleted} utenti test eliminati con cascade.`,
      });
      // Broadcast so other pages refresh their feeds.
      try { window.dispatchEvent(new Event('nymia:data-changed')); } catch (_e) { /* noop */ }
    } catch (e) {
      setError(e?.message || String(e));
      toast({ title: 'Cleanup fallita', description: e?.message || String(e), variant: 'destructive' });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="clean-test-data-panel">
      <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3">
        <p className="text-xs font-semibold text-red-700">
          ⚠️ Azione distruttiva — elimina definitivamente
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground">
          Cascade-delete su ogni utente con flag <code className="rounded bg-secondary px-1">is_test=true</code> più le sue
          live, post, like, follow, commenti, prenotazioni, notifiche, conversazioni e payouts.
          L&apos;admin reale e ogni utente con <code>is_test=false</code> rimangono intatti.
          Il backup automatico in <code>/app/backups/</code> è stato già creato in Phase 25.
        </p>
      </div>

      {!confirming ? (
        <Button
          onClick={() => setConfirming(true)}
          variant="destructive"
          size="sm"
          disabled={running}
          data-testid="clean-test-data-btn"
        >
          {running ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1 h-4 w-4" />}
          {running ? 'Cleanup in corso…' : 'Clean Test Data'}
        </Button>
      ) : (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3" data-testid="clean-test-data-confirm">
          <p className="text-xs font-semibold text-amber-900">
            Confermi l&apos;eliminazione di TUTTI i dati con flag is_test=true?
          </p>
          <p className="mt-1 text-[11px] text-amber-800">L&apos;operazione non è reversibile.</p>
          <div className="mt-2 flex gap-2">
            <Button onClick={run} variant="destructive" size="sm" data-testid="clean-test-data-confirm-btn">
              Sì, esegui ora
            </Button>
            <Button onClick={() => setConfirming(false)} variant="outline" size="sm" data-testid="clean-test-data-cancel-btn">
              Annulla
            </Button>
          </div>
        </div>
      )}

      {running && (
        <div className="flex items-center gap-2 rounded-md border border-border bg-background/40 p-2 text-xs">
          <Loader2 className="h-4 w-4 animate-spin gold-text" />
          Eliminazione in corso… (può richiedere fino a 30 secondi)
        </div>
      )}

      {error && (
        <p className="rounded-md bg-red-500/10 px-2 py-1 text-xs text-red-700" data-testid="clean-test-data-error">
          ❌ {error}
        </p>
      )}

      {result && (
        <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3" data-testid="clean-test-data-result">
          <p className="text-sm font-semibold text-emerald-800">
            ✅ Cleanup completata — {result.test_users_deleted} utenti test eliminati
          </p>
          <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-emerald-900 sm:grid-cols-3">
            {Object.entries(result.cascade || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-emerald-500/20 py-0.5" data-testid={`cleanup-row-${k}`}>
                <span className="font-mono text-emerald-700">{k}</span>
                <span className={`font-bold ${typeof v === 'string' ? 'text-red-700' : ''}`}>{String(v)}</span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[10px] text-emerald-700/80">Home, Explore, Lives e cerchi Stories sono stati notificati e si aggiorneranno al prossimo refresh.</p>
        </div>
      )}
    </div>
  );
};


/** Phase 26.D — Legacy Purge Panel.
 *  One-click tool for the production scenario: the user has legacy test
 *  users (pippo, qa_creator_*, testuser*, creator_<hex>, demo*) that
 *  predate the `is_test` flag system and therefore cannot be cleaned by
 *  the standard "Clean Test Data" button. This panel:
 *    1. Calls /api/admin/cleanup/purge-by-pattern WITHOUT confirm to get
 *       a dry-run preview of what would be deleted.
 *    2. Shows the list to the operator (so they can verify nothing
 *       legitimate is in there).
 *    3. On confirmation, re-calls with ?confirm=YES to actually purge.
 *  Hard-protected: admin@instagram.local, role=admin, ADMIN_EMAIL env,
 *  and the calling admin themselves are never deleted.
 */
const LEGACY_PATTERN = '^(pippo|qa_creator.*|testuser.*|demo\\d+|creator_[a-f0-9]+|test.*|phase26.*|livedemo.*|pay[a-f0-9]+|super|unknown|fixture.*|seed_.*)$';

const LegacyPurgePanel = () => {
  const { toast } = useToast();
  const [phase, setPhase] = React.useState('idle'); // idle | preview | confirming | running | done
  const [preview, setPreview] = React.useState(null);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');

  const callApi = async (confirm) => {
    const tok = localStorage.getItem('ig_token');
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/cleanup/purge-by-pattern${confirm ? '?confirm=YES' : ''}`;
    const r = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
      body: JSON.stringify({ username_regex: LEGACY_PATTERN }),
    });
    const j = await r.json();
    if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
    return j;
  };

  const doPreview = async () => {
    setError(''); setResult(null); setPreview(null); setPhase('running');
    try {
      const j = await callApi(false);
      setPreview(j);
      setPhase('preview');
    } catch (e) {
      setError(e?.message || String(e));
      setPhase('idle');
    }
  };

  const doPurge = async () => {
    setPhase('running');
    try {
      const j = await callApi(true);
      setResult(j);
      setPhase('done');
      toast({ title: 'Purge eseguita', description: `${j.deleted_users} utenti legacy eliminati.` });
      try { window.dispatchEvent(new Event('nymia:data-changed')); } catch (_e) { /* noop */ }
    } catch (e) {
      setError(e?.message || String(e));
      setPhase('preview');
    }
  };

  return (
    <div className="space-y-3" data-testid="legacy-purge-panel">
      <div className="rounded-md border border-orange-500/40 bg-orange-500/5 p-3">
        <p className="text-xs font-semibold text-orange-700">
          🧨 Purge legacy test users (no `is_test` flag required)
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground">
          Per i vecchi account creati PRIMA del sistema <code>is_test</code> (pippo, qa_creator_*, testuser*,
          creator_&lt;hex&gt;, demo3, phase26*, livedemo*). Cancella anche le loro live, post, follow, ecc.
          Protetto: admin@instagram.local, role=admin/super_admin/owner, ADMIN_EMAIL env, e te stesso.
        </p>
        <p className="mt-1 text-[10px] font-mono text-orange-700/70 break-all">
          regex: {LEGACY_PATTERN}
        </p>
      </div>

      {phase === 'idle' && (
        <Button
          onClick={doPreview}
          variant="outline"
          size="sm"
          className="border-orange-500/50 text-orange-700 hover:bg-orange-50"
          data-testid="legacy-purge-preview-btn"
        >
          1️⃣ Anteprima utenti da cancellare
        </Button>
      )}

      {phase === 'running' && (
        <div className="flex items-center gap-2 rounded-md border border-border bg-background/40 p-2 text-xs">
          <Loader2 className="h-4 w-4 animate-spin gold-text" />
          {preview ? 'Eliminazione in corso…' : 'Verifica in corso…'}
        </div>
      )}

      {phase === 'preview' && preview && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3" data-testid="legacy-purge-preview">
          <p className="text-xs font-semibold text-amber-900">
            Sta per essere ELIMINATO ({preview.would_delete_count} utenti):
          </p>
          {preview.would_delete_count === 0 ? (
            <p className="mt-2 text-xs text-amber-800">
              ✅ Nessun utente legacy trovato. Il database è già pulito.
            </p>
          ) : (
            <ul className="mt-2 max-h-48 list-disc overflow-auto pl-5 text-xs text-amber-900" data-testid="legacy-purge-preview-list">
              {(preview.would_delete_preview || []).map((u) => (
                <li key={u} className="font-mono">@{u}</li>
              ))}
            </ul>
          )}
          {preview.protected_skipped?.length > 0 && (
            <p className="mt-2 text-[11px] text-emerald-800">
              🛡️ Protetti (non verranno toccati): {preview.protected_skipped.join(', ')}
            </p>
          )}
          {preview.would_delete_count > 0 && (
            <div className="mt-3 flex gap-2">
              <Button
                onClick={doPurge}
                variant="destructive"
                size="sm"
                data-testid="legacy-purge-confirm-btn"
              >
                2️⃣ Sì, elimina definitivamente
              </Button>
              <Button onClick={() => { setPhase('idle'); setPreview(null); }} variant="outline" size="sm" data-testid="legacy-purge-cancel-btn">
                Annulla
              </Button>
            </div>
          )}
        </div>
      )}

      {phase === 'done' && result && (
        <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3" data-testid="legacy-purge-result">
          <p className="text-sm font-semibold text-emerald-800">
            ✅ Purge completata — {result.deleted_users} utenti legacy eliminati
          </p>
          <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-emerald-900 sm:grid-cols-3">
            {Object.entries(result.cascade || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-emerald-500/20 py-0.5">
                <span className="font-mono text-emerald-700">{k}</span>
                <span className="font-bold">{String(v)}</span>
              </div>
            ))}
          </div>
          {result.purged_usernames?.length > 0 && (
            <details className="mt-2 text-[11px] text-emerald-900">
              <summary className="cursor-pointer">Lista utenti eliminati ({result.purged_usernames.length})</summary>
              <ul className="mt-1 max-h-32 list-disc overflow-auto pl-5 font-mono">
                {result.purged_usernames.map((u) => (<li key={u}>@{u}</li>))}
              </ul>
            </details>
          )}
          <Button
            onClick={() => { setPhase('idle'); setPreview(null); setResult(null); }}
            variant="outline"
            size="sm"
            className="mt-3"
            data-testid="legacy-purge-reset-btn"
          >
            Riparti
          </Button>
        </div>
      )}

      {error && (
        <p className="rounded-md bg-red-500/10 px-2 py-1 text-xs text-red-700" data-testid="legacy-purge-error">
          ❌ {error}
        </p>
      )}
    </div>
  );
};


/** Phase 26.H — User Login Diagnostic Panel.
 *  When a real user reports "I can't log in", this panel queries
 *  /api/admin/users/diagnose-by-email and renders the full account state
 *  (is_test, status, password_hash kind, email_verified, last_login)
 *  along with plain-language reasons that might be blocking the login.
 *  No mutation happens here — only diagnosis. A separate "Reset password"
 *  action is exposed once the diagnosis is shown, behind a confirmation.
 */
const UserLoginDiagnosticPanel = () => {
  const { toast } = useToast();
  const [email, setEmail] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');
  // Reset password state
  const [confirmingReset, setConfirmingReset] = React.useState(false);
  const [newPw, setNewPw] = React.useState('');
  const [resetting, setResetting] = React.useState(false);
  const [resetResult, setResetResult] = React.useState(null);

  const runDiagnose = async () => {
    const e = email.trim().toLowerCase();
    if (!e) {
      toast({ title: 'Inserisci un\u2019email', variant: 'destructive' });
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    setResetResult(null);
    setConfirmingReset(false);
    try {
      const tok = localStorage.getItem('ig_token');
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/diagnose-by-email?email=${encodeURIComponent(e)}`,
        { headers: { Authorization: `Bearer ${tok}` } },
      );
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
      setResult(j);
    } catch (e2) {
      setError(e2?.message || String(e2));
      toast({ title: 'Diagnosi fallita', description: e2?.message || String(e2), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const runResetPassword = async () => {
    if (!result?.exists) return;
    setResetting(true);
    try {
      const tok = localStorage.getItem('ig_token');
      const body = { email: result.email };
      if (newPw && newPw.trim().length >= 6) body.new_password = newPw.trim();
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
      setResetResult(j);
      setConfirmingReset(false);
      toast({ title: 'Password reimpostata', description: `Nuova password generata per ${j.email}.` });
    } catch (e2) {
      toast({ title: 'Reset fallito', description: e2?.message || String(e2), variant: 'destructive' });
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="user-login-diag-panel">
      <div className="rounded-md border border-blue-500/30 bg-blue-500/5 p-3">
        <p className="text-xs font-semibold text-blue-700">
          🔐 Diagnosi login utente
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground">
          Quando un utente reale dice &quot;non riesco a loggare&quot;, inserisci la sua email qui per ottenere il dump
          dello stato account: <code>exists</code>, <code>is_test</code>, <code>status</code>,{' '}
          <code>password_hash</code> (bcrypt/missing), <code>email_verified</code> e le cause plausibili del blocco.
          Nessun dato viene modificato finch&eacute; non clicchi &quot;Reset password&quot;.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Input
          type="email"
          placeholder="utente@dominio.it"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1 min-w-[220px]"
          data-testid="user-diag-email-input"
        />
        <Button
          onClick={runDiagnose}
          disabled={loading || !email.trim()}
          size="sm"
          data-testid="user-diag-run-btn"
        >
          {loading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Stethoscope className="mr-1 h-4 w-4" />}
          {loading ? 'Diagnosi…' : 'Diagnosi'}
        </Button>
      </div>

      {error && (
        <p className="rounded-md bg-red-500/10 px-2 py-1 text-xs text-red-700" data-testid="user-diag-error">
          ❌ {error}
        </p>
      )}

      {result && !result.exists && (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 p-3" data-testid="user-diag-not-found">
          <p className="text-sm font-semibold text-red-800">
            ❌ Utente NON trovato per email <code>{result.email_searched}</code>
          </p>
          <p className="mt-1 text-xs text-red-700">{result.hint}</p>
          {result.alt_match && (
            <div className="mt-2 rounded bg-background/40 p-2 text-[11px] text-foreground">
              <p>🔎 Match alternativo case-insensitive:</p>
              <p>• id: <code>{result.alt_match.id}</code></p>
              <p>• email: <code>{result.alt_match.email}</code></p>
              <p>• username: <code>{result.alt_match.username}</code></p>
            </div>
          )}
        </div>
      )}

      {result && result.exists && (
        <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 space-y-2" data-testid="user-diag-found">
          <p className="text-sm font-semibold text-emerald-800">
            ✅ Utente trovato — <code>{result.username}</code>
          </p>
          <div className="grid grid-cols-1 gap-x-4 gap-y-1 text-[11px] text-emerald-900 sm:grid-cols-2">
            <div><span className="font-mono text-emerald-700">id:</span> <code>{result.id}</code></div>
            <div><span className="font-mono text-emerald-700">email:</span> <code>{result.email}</code></div>
            <div><span className="font-mono text-emerald-700">username:</span> <code>{result.username}</code></div>
            <div><span className="font-mono text-emerald-700">role:</span> {String(result.role)}</div>
            <div><span className="font-mono text-emerald-700">status:</span> <code>{String(result.status)}</code></div>
            <div><span className="font-mono text-emerald-700">is_test:</span> <code>{String(result.is_test)}</code></div>
            <div><span className="font-mono text-emerald-700">email_verified:</span> <code>{String(result.email_verified)}</code></div>
            <div><span className="font-mono text-emerald-700">has_password_hash:</span> <code>{String(result.has_password_hash)}</code></div>
            <div><span className="font-mono text-emerald-700">password_hash_kind:</span> <code>{result.password_hash_kind}</code></div>
            <div><span className="font-mono text-emerald-700">created_at:</span> <code>{result.created_at || '—'}</code></div>
            <div><span className="font-mono text-emerald-700">last_login_at:</span> <code>{result.last_login_at || '—'}</code></div>
          </div>

          {Array.isArray(result.diagnosis) && result.diagnosis.length > 0 && (
            <div className="rounded bg-background/40 p-2 text-[12px]">
              <p className="font-semibold text-foreground mb-1">Cause plausibili del blocco login:</p>
              <ul className="list-none space-y-1">
                {result.diagnosis.map((line, i) => (
                  <li key={i} className="font-mono text-[11px]">{line}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Reset password action */}
          <div className="border-t border-emerald-500/30 pt-2">
            {!confirmingReset && !resetResult && (
              <Button
                onClick={() => setConfirmingReset(true)}
                variant="outline"
                size="sm"
                disabled={resetting}
                data-testid="user-diag-reset-pw-btn"
              >
                <KeyRound className="mr-1 h-4 w-4" />
                Reset password
              </Button>
            )}

            {confirmingReset && (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 space-y-2" data-testid="user-diag-reset-confirm">
                <p className="text-xs font-semibold text-amber-900">
                  Confermi il reset password per <code>{result.email}</code>?
                </p>
                <p className="text-[11px] text-amber-800">
                  Lascia il campo vuoto per generare una password random di 12 caratteri. La password verr&agrave; mostrata in chiaro solo qui.
                </p>
                <Input
                  type="text"
                  placeholder="(opzionale) nuova password — min 6 caratteri"
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  data-testid="user-diag-reset-pw-input"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={runResetPassword}
                    variant="destructive"
                    size="sm"
                    disabled={resetting}
                    data-testid="user-diag-reset-pw-confirm-btn"
                  >
                    {resetting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <KeyRound className="mr-1 h-4 w-4" />}
                    {resetting ? 'Reset in corso…' : 'S&igrave;, reimposta ora'}
                  </Button>
                  <Button
                    onClick={() => { setConfirmingReset(false); setNewPw(''); }}
                    variant="outline"
                    size="sm"
                    disabled={resetting}
                    data-testid="user-diag-reset-pw-cancel-btn"
                  >
                    Annulla
                  </Button>
                </div>
              </div>
            )}

            {resetResult && (
              <div className="rounded-md border border-emerald-500/40 bg-emerald-500/15 p-3" data-testid="user-diag-reset-result">
                <p className="text-sm font-semibold text-emerald-900">
                  ✅ Password reimpostata per <code>{resetResult.email}</code>
                </p>
                <p className="mt-1 text-[11px] text-emerald-800">
                  Nuova password (mostra una volta):
                </p>
                <code className="mt-1 block rounded bg-background/60 px-2 py-1 font-mono text-sm text-foreground" data-testid="user-diag-new-password">
                  {resetResult.new_password}
                </code>
                <p className="mt-2 text-[11px] text-emerald-700/80">
                  Condividila con l&apos;utente. Deve cambiarla al primo accesso.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};



/** Phase 26.D — Live Visibility Diagnostic Panel.
 *  Lists the 20 most recent lives in the system and, for each, explains
 *  whether it appears in the public feeds and — if not — exactly which
 *  filter blocked it. Provides one-click fix buttons for the common
 *  blockers: creator flagged `is_test=True`, live set to private, etc.
 *  Designed for production use when "my live doesn't appear anywhere".
 */
const LiveDiagnosticPanel = () => {
  const { toast } = useToast();
  const [loading, setLoading] = React.useState(false);
  const [lives, setLives] = React.useState([]);
  const [me, setMe] = React.useState(null);
  const [fixing, setFixing] = React.useState('');
  const [error, setError] = React.useState('');

  const refresh = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const tok = localStorage.getItem('ig_token');
      const headers = { Authorization: `Bearer ${tok}` };
      const [r1, r2] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/diag/recent-lives`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/diag/dump`, { headers }),
      ]);
      const j1 = await r1.json();
      const j2 = await r2.json();
      if (!r1.ok) throw new Error(j1?.detail || `HTTP ${r1.status}`);
      setLives(j1.lives || []);
      setMe(j2?.samples?.calling_admin || null);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  const unflag = async (userId) => {
    setFixing(`unflag-${userId}`);
    try {
      const tok = localStorage.getItem('ig_token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/unflag-test`, {
        method: 'POST', headers: { Authorization: `Bearer ${tok}` },
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
      toast({ title: 'Voce Pro sbloccata', description: 'is_test=False — refresh in corso.' });
      try { window.dispatchEvent(new Event('nymia:data-changed')); } catch (_e) { /* noop */ }
      await refresh();
    } catch (e) {
      toast({ title: 'Fix fallito', description: e?.message || String(e), variant: 'destructive' });
    } finally {
      setFixing('');
    }
  };

  const makePublic = async (liveId) => {
    setFixing(`public-${liveId}`);
    try {
      const tok = localStorage.getItem('ig_token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/lives/${liveId}/force-public`, {
        method: 'POST', headers: { Authorization: `Bearer ${tok}` },
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
      toast({ title: 'Privacy → public' });
      try { window.dispatchEvent(new Event('nymia:data-changed')); } catch (_e) { /* noop */ }
      await refresh();
    } catch (e) {
      toast({ title: 'Fix fallito', description: e?.message || String(e), variant: 'destructive' });
    } finally {
      setFixing('');
    }
  };

  const forceClose = async (liveId) => {
    setFixing(`close-${liveId}`);
    try {
      const tok = localStorage.getItem('ig_token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/lives/${liveId}/force-close`, {
        method: 'POST', headers: { Authorization: `Bearer ${tok}` },
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
      toast({ title: 'Live chiusa' });
      try { window.dispatchEvent(new Event('nymia:data-changed')); } catch (_e) { /* noop */ }
      await refresh();
    } catch (e) {
      toast({ title: 'Fix fallito', description: e?.message || String(e), variant: 'destructive' });
    } finally {
      setFixing('');
    }
  };

  const closeAllStale = async () => {
    setFixing('close-all-stale');
    try {
      const tok = localStorage.getItem('ig_token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cleanup/close-stale-lives`, {
        method: 'POST', headers: { Authorization: `Bearer ${tok}` },
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
      const total = j.total_closed || 0;
      toast({ title: 'Stale lives chiuse', description: `${total} live marcate come ended.` });
      try { window.dispatchEvent(new Event('nymia:data-changed')); } catch (_e) { /* noop */ }
      await refresh();
    } catch (e) {
      toast({ title: 'Fix fallito', description: e?.message || String(e), variant: 'destructive' });
    } finally {
      setFixing('');
    }
  };

  const syncLivekit = async () => {
    setFixing('sync-livekit');
    try {
      const tok = localStorage.getItem('ig_token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cleanup/sync-livekit-rooms`, {
        method: 'POST', headers: { Authorization: `Bearer ${tok}` },
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
      toast({
        title: 'LiveKit sync completata',
        description: `Verificate ${j.checked} live, ${j.closed} chiuse perché stanza vuota.`,
      });
      try { window.dispatchEvent(new Event('nymia:data-changed')); } catch (_e) { /* noop */ }
      await refresh();
    } catch (e) {
      toast({ title: 'Sync LiveKit fallito', description: e?.message || String(e), variant: 'destructive' });
    } finally {
      setFixing('');
    }
  };

  const fixDuplicates = async () => {
    setFixing('fix-duplicates');
    try {
      const tok = localStorage.getItem('ig_token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cleanup/fix-duplicate-active-lives`, {
        method: 'POST', headers: { Authorization: `Bearer ${tok}` },
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
      toast({
        title: `Duplicati risolti`,
        description: `${j.creators_affected} Voci Pro interessate, ${j.lives_closed_count} live chiuse.`,
      });
      try { window.dispatchEvent(new Event('nymia:data-changed')); } catch (_e) { /* noop */ }
      await refresh();
    } catch (e) {
      toast({ title: 'Fix duplicati fallito', description: e?.message || String(e), variant: 'destructive' });
    } finally {
      setFixing('');
    }
  };

  const meWarning = me?.is_test;
  // Counts per classification (drives bulk-action buttons)
  const counts = lives.reduce((acc, l) => {
    acc[l.classification] = (acc[l.classification] || 0) + 1;
    return acc;
  }, {});
  // Phase 26.F — detect duplicate active lives (same creator > 1)
  const activeByCreator = lives
    .filter((l) => l.classification === 'active')
    .reduce((acc, l) => {
      const k = l.creator?.id || '?';
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {});
  const duplicateActiveTotal = Object.values(activeByCreator).reduce(
    (s, n) => s + (n > 1 ? n - 1 : 0),
    0,
  );
  const classBadge = (cls) => {
    const map = {
      active:    { bg: 'bg-emerald-500/20', text: 'text-emerald-700', label: '✅ attiva' },
      scheduled: { bg: 'bg-blue-500/15',    text: 'text-blue-700',    label: '🟡 programmata' },
      ended:     { bg: 'bg-secondary',       text: 'text-muted-foreground', label: '⚪ ended' },
      stale:     { bg: 'bg-red-500/20',     text: 'text-red-700',     label: '🔴 stale' },
      orphan:    { bg: 'bg-red-500/30',     text: 'text-red-700',     label: '🔴 orphan' },
      fake:      { bg: 'bg-red-500/30',     text: 'text-red-700',     label: '🔴 fake' },
    };
    const v = map[cls] || map.ended;
    return <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${v.bg} ${v.text}`}>{v.label}</span>;
  };

  return (
    <div className="space-y-3" data-testid="live-diag-panel">
      <div className="rounded-md border border-blue-500/30 bg-blue-500/5 p-3">
        <p className="text-xs font-semibold text-blue-700">
          🔍 Diagnostica visibilità live
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground">
          Per ogni live recente, ti mostro se appare nei feed pubblici (Home / Live Now / Explore / Profilo).
          Se è bloccata, ti mostro <strong>quale filtro</strong> la blocca e ti do un pulsante di fix immediato.
        </p>
      </div>

      {meWarning && (
        <div className="rounded-md border-2 border-red-500/60 bg-red-500/10 p-3" data-testid="live-diag-me-warning">
          <p className="text-sm font-bold text-red-700">
            ⚠️ ATTENZIONE — Il TUO admin è flaggato is_test=True
          </p>
          <p className="mt-1 text-[11px] text-red-700">
            Username: <code className="font-mono">@{me.username}</code> · Email: <code className="font-mono">{me.email}</code><br/>
            <strong>Tutte le tue live e i tuoi post sono invisibili nei feed pubblici.</strong>
            È la causa più probabile del bug «live non appare». Premi sotto per sbloccarti.
          </p>
          <Button
            onClick={() => unflag(me.id)}
            variant="destructive"
            size="sm"
            disabled={fixing === `unflag-${me.id}`}
            className="mt-2"
            data-testid="live-diag-unflag-me-btn"
          >
            {fixing === `unflag-${me.id}` ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            🔓 Sblocca me stesso ora
          </Button>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Button onClick={refresh} variant="outline" size="sm" disabled={loading} data-testid="live-diag-refresh-btn">
            {loading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            {loading ? 'Caricamento…' : '🔄 Ricarica'}
          </Button>
          {(counts.stale > 0 || counts.orphan > 0 || counts.fake > 0) && (
            <Button
              onClick={closeAllStale}
              variant="destructive"
              size="sm"
              disabled={fixing === 'close-all-stale'}
              data-testid="live-diag-close-all-stale-btn"
            >
              {fixing === 'close-all-stale' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              🧹 Chiudi tutte le stale ({(counts.stale || 0) + (counts.orphan || 0) + (counts.fake || 0)})
            </Button>
          )}
          <Button
            onClick={syncLivekit}
            variant="outline"
            size="sm"
            disabled={fixing === 'sync-livekit'}
            className="border-blue-500/50 text-blue-700"
            data-testid="live-diag-sync-livekit-btn"
          >
            {fixing === 'sync-livekit' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            🛰️ Sync LiveKit (chiudi stanze vuote)
          </Button>
          {duplicateActiveTotal > 0 && (
            <Button
              onClick={fixDuplicates}
              variant="destructive"
              size="sm"
              disabled={fixing === 'fix-duplicates'}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="live-diag-fix-duplicates-btn"
            >
              {fixing === 'fix-duplicates' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              🔧 Fix duplicate active lives ({duplicateActiveTotal})
            </Button>
          )}
        </div>
        {me && !meWarning && (
          <span className="text-[11px] text-emerald-700">
            ✓ Il tuo admin (@{me.username}) NON è flaggato test
          </span>
        )}
      </div>

      {/* Classification summary */}
      {lives.length > 0 && (
        <div className="flex flex-wrap gap-2 text-[11px]" data-testid="live-diag-counts">
          {Object.entries({active: '✅', scheduled: '🟡', ended: '⚪', stale: '🔴', orphan: '🔴', fake: '🔴'}).map(([k, ico]) => (
            <span key={k} className="rounded-full bg-secondary px-2 py-0.5">
              {ico} {k}: <strong>{counts[k] || 0}</strong>
            </span>
          ))}
        </div>
      )}

      {error && <p className="text-xs text-red-700">❌ {error}</p>}

      <div className="rounded-md border border-border bg-card">
        {lives.length === 0 && !loading && (
          <p className="p-3 text-xs text-muted-foreground">Nessuna live trovata.</p>
        )}
        {lives.map((lv) => (
          <div key={lv.id} className="border-b border-border p-3 last:border-b-0" data-testid={`live-diag-row-${lv.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{lv.title || '(no title)'}</p>
                <div className="mt-1 flex flex-wrap gap-1 text-[10px]">
                  <span className={`rounded px-1.5 py-0.5 font-mono font-bold ${lv.status === 'live' ? 'bg-red-500/20 text-red-700' : 'bg-secondary text-muted-foreground'}`}>
                    {lv.status}
                  </span>
                  <span className={`rounded px-1.5 py-0.5 font-mono ${lv.privacy === 'public' ? 'bg-emerald-500/15 text-emerald-700' : 'bg-amber-500/20 text-amber-800'}`}>
                    {lv.privacy}
                  </span>
                  <span className="rounded px-1.5 py-0.5 font-mono bg-secondary text-muted-foreground">
                    @{lv.creator?.username || 'unknown'}
                  </span>
                  {lv.creator?.is_test && (
                    <span className="rounded px-1.5 py-0.5 font-mono bg-red-500/30 text-red-700 font-bold">
                      Voce Pro is_test=TRUE
                    </span>
                  )}
                  {lv.is_stale_closed && (
                    <span className="rounded px-1.5 py-0.5 font-mono bg-red-500/30 text-red-700 font-bold">
                      stale_closed:{lv.stale_closed_reason}
                    </span>
                  )}
                  <span className="rounded px-1.5 py-0.5 font-mono bg-secondary text-muted-foreground">
                    id={lv.id?.slice(0, 8)}
                  </span>
                </div>
              </div>
              <div className="shrink-0 flex flex-col items-end gap-1">
                {classBadge(lv.classification)}
                {lv.feeds_visible ? (
                  <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-700" data-testid={`live-diag-visible-${lv.id}`}>
                    ✓ visibile nei feed
                  </span>
                ) : (
                  <span className="rounded-full bg-red-500/10 px-2 py-0.5 text-[10px] text-red-700" data-testid={`live-diag-blocked-${lv.id}`}>
                    ✗ bloccata
                  </span>
                )}
              </div>
            </div>
            {!lv.feeds_visible && lv.blocked_by?.length > 0 && (
              <div className="mt-2 rounded bg-red-500/5 p-2">
                <p className="text-[11px] font-semibold text-red-700">Bloccata da:</p>
                <ul className="ml-4 list-disc text-[11px] text-red-700">
                  {lv.blocked_by.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
                <div className="mt-2 flex flex-wrap gap-2">
                  {lv.actions?.can_unflag_creator && (
                    <Button onClick={() => unflag(lv.creator.id)} variant="outline" size="sm" disabled={fixing === `unflag-${lv.creator.id}`} className="h-7 text-[11px]" data-testid={`live-diag-unflag-${lv.creator.id}`}>
                      🔓 Sblocca @{lv.creator.username}
                    </Button>
                  )}
                  {lv.actions?.can_make_public && (
                    <Button onClick={() => makePublic(lv.id)} variant="outline" size="sm" disabled={fixing === `public-${lv.id}`} className="h-7 text-[11px]" data-testid={`live-diag-public-${lv.id}`}>
                      🌐 Rendi pubblica
                    </Button>
                  )}
                  {lv.actions?.can_close_stale && (
                    <Button onClick={() => forceClose(lv.id)} variant="outline" size="sm" disabled={fixing === `close-${lv.id}`} className="h-7 text-[11px]" data-testid={`live-diag-close-${lv.id}`}>
                      🛑 Chiudi (mark ended)
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};



const StatBox = ({ icon: Icon, label, value, color }) => (
  <div className="rounded-xl border border-border bg-card p-4">
    <div className={`mb-2 flex h-8 w-8 items-center justify-center rounded-lg ${color}`}>
      <Icon className="h-4 w-4" />
    </div>
    <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="mt-1 text-xl font-semibold tabular-nums text-foreground">{value}</p>
  </div>
);

const TabBtn = ({ active, onClick, icon: Icon, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold ${
      active ? 'gold-gradient' : 'text-foreground hover:bg-secondary'
    }`}
  >
    <Icon className="h-4 w-4" /> {children}
  </button>
);

const UserRow = ({ user, onSaveFee, onSetStatus, onPause, onResume, onDelete, onSuspendMonet, onResumeMonet, onSetRole, canManageRoles, onOpenBilling, onOpenWallet, onStandby, onReleaseStandby, onPreviewCard }) => {
  const [editing, setEditing] = useState(false);
  const [fee, setFee] = useState(user.platform_fee_percentage.toString());
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const f = parseFloat(fee);
    if (isNaN(f) || f < 0 || f > 100) return;
    setSaving(true);
    await onSaveFee(user.id, f);
    setSaving(false);
    setEditing(false);
  };

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  const status = user.status || 'active';
  const isPaused = status === 'paused';
  const statusColors = {
    active: 'bg-emerald-500/20 text-emerald-300',
    paused: 'bg-amber-500/20 gold-text',
    deleted: 'bg-red-500/20 text-red-300',
  };

  return (
    <tr className="border-b border-border last:border-b-0">
      <td className="px-4 py-3">
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-foreground">
            @{user.username}
            {user.user_number ? (
              <span className="ml-2 inline-flex items-center rounded-md bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold gold-text" data-testid={`admin-user-number-${user.id}`}>
                #{user.user_number}
              </span>
            ) : null}
          </span>
          <span className="text-xs text-muted-foreground">{user.full_name}</span>
          {user.created_at && (
            <span
              className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-muted-foreground/80"
              title="Data di registrazione"
              data-testid={`admin-user-registered-${user.id}`}
            >
              <CalendarDays className="h-3 w-3 gold-text" />
              Registrato il {new Date(user.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-muted-foreground">{user.email}</td>
      <td className="px-4 py-3 text-center text-sm tabular-nums text-foreground">{user.posts_count}</td>
      <td className="px-4 py-3 text-right text-sm tabular-nums text-foreground">
        <div className="flex flex-col items-end gap-1">
          <button
            onClick={() => onOpenWallet && onOpenWallet(user)}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 hover:border-amber-500/40 hover:bg-amber-500/10"
            title="Gestisci wallet manualmente"
            data-testid={`admin-wallet-btn-${user.id}`}
          >
            <Coins className="h-3 w-3 gold-text" />
            <span>{Math.round(user.wallet_balance).toLocaleString()} cr</span>
          </button>
          <button
            onClick={() => onOpenWallet && onOpenWallet(user)}
            className="inline-flex items-center gap-1 rounded-full bg-amber-400 px-2.5 py-0.5 text-[10px] font-bold text-black hover:bg-amber-300"
            title="Aggiungi o sottrai crediti a questo utente"
            data-testid={`admin-add-credits-${user.id}`}
          >
            <Plus className="h-3 w-3" /> Aggiungi crediti
          </button>
        </div>
      </td>
      <td className="px-4 py-3">
        {editing ? (
          <div className="flex items-center gap-1.5">
            <Input
              type="number" step="0.5" min="0" max="100"
              value={fee} onChange={(e) => setFee(e.target.value)}
              className="h-8 w-20"
            />
            <span className="text-xs">%</span>
            <button onClick={save} disabled={saving} className="rounded-md bg-emerald-500 p-1.5 text-white hover:bg-emerald-600 disabled:opacity-50"><Check className="h-3.5 w-3.5" /></button>
            <button onClick={() => { setEditing(false); setFee(user.platform_fee_percentage.toString()); }} className="rounded-md bg-secondary p-1.5 hover:bg-muted"><X className="h-3.5 w-3.5" /></button>
          </div>
        ) : (
          <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-sm hover:bg-secondary">
            <span className="font-semibold tabular-nums gold-text">{user.platform_fee_percentage.toFixed(1)}%</span>
            <Pencil className="h-3 w-3 text-muted-foreground" />
          </button>
        )}
      </td>
      <td className="px-4 py-3">
        <select
          value={user.creator_status === 'active' ? 'active' : 'none'}
          onChange={(e) => onSetStatus(user.id, e.target.value)}
          className="h-8 rounded-md border border-border bg-card px-2 text-xs"
        >
          <option value="active">active</option>
          <option value="none">none</option>
        </select>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {!user.is_admin && (
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${statusColors[status] || 'bg-secondary text-foreground'}`}
              data-testid={`user-status-badge-${user.id}`}
            >
              {status}
            </span>
          )}
          {!user.is_admin && user.standby && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-orange-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase text-orange-300"
              title={user.standby_reason || 'In standby: nascosto dai feed'}
              data-testid={`user-standby-badge-${user.id}`}
            >
              <EyeOff className="h-3 w-3" /> Standby
            </span>
          )}
          {user.is_admin && (
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                (user.role || 'admin') === 'admin'
                  ? 'bg-violet-500/20 text-violet-300'
                  : 'bg-sky-500/20 text-sky-300'
              }`}
              data-testid={`user-role-badge-${user.id}`}
            >
              <ShieldCheck className="h-3 w-3" /> {(user.role || 'admin').toUpperCase()}
            </span>
          )}
          {/* Phase 42 — quick access to Stripe/fiscal billing profile */}
          <button
            onClick={() => onOpenBilling && onOpenBilling(user.id)}
            title="Stripe & fiscal profile"
            className="inline-flex items-center gap-1 rounded-md border border-amber-500/30 bg-amber-500/10 px-1.5 py-1 text-[10px] font-semibold gold-text hover:bg-amber-500/20"
            data-testid={`admin-stripe-btn-${user.id}`}
          >
            <Landmark className="h-3 w-3" />
            Stripe
          </button>
          {canManageRoles && (
            <select
              value={user.role || (user.is_admin ? 'admin' : 'user')}
              onChange={(e) => onSetRole(user.id, e.target.value)}
              className="ml-1 rounded-md border border-border bg-card px-1.5 py-0.5 text-[10px] font-semibold text-foreground"
              title="Set role"
              data-testid={`admin-role-select-${user.id}`}
            >
              <option value="user">user</option>
              <option value="staff">staff</option>
              <option value="admin">admin</option>
            </select>
          )}
          {!user.is_admin && (
            <div className="flex gap-1">
              <button
                onClick={() => onPreviewCard && onPreviewCard(user)}
                disabled={busy}
                title="Anteprima card Persone (con audio)"
                className="rounded-md bg-sky-500/15 p-1.5 text-sky-300 hover:bg-sky-500/30 disabled:opacity-50"
                data-testid={`admin-preview-card-${user.id}`}
              >
                <IdCard className="h-3.5 w-3.5" />
              </button>
              {user.standby ? (
                <button
                  onClick={() => run(() => onReleaseStandby(user.id))}
                  disabled={busy}
                  title="Riattiva nei feed (togli standby)"
                  className="rounded-md bg-orange-500/15 p-1.5 text-orange-300 hover:bg-orange-500/30 disabled:opacity-50"
                  data-testid={`admin-release-standby-${user.id}`}
                >
                  <Eye className="h-3.5 w-3.5" />
                </button>
              ) : (
                <button
                  onClick={() => run(() => onStandby(user.id, user.username))}
                  disabled={busy}
                  title="Metti in standby (nascosto dai feed, può accedere e modificare)"
                  className="rounded-md bg-orange-500/15 p-1.5 text-orange-300 hover:bg-orange-500/30 disabled:opacity-50"
                  data-testid={`admin-standby-user-${user.id}`}
                >
                  <EyeOff className="h-3.5 w-3.5" />
                </button>
              )}
              {isPaused ? (
                <button
                  onClick={() => run(() => onResume(user.id))}
                  disabled={busy}
                  title="Resume account"
                  className="rounded-md bg-emerald-500/15 p-1.5 text-emerald-300 hover:bg-emerald-500/30 disabled:opacity-50"
                  data-testid={`admin-resume-user-${user.id}`}
                >
                  <Play className="h-3.5 w-3.5" />
                </button>
              ) : (
                <button
                  onClick={() => run(() => onPause(user.id))}
                  disabled={busy}
                  title="Pause account"
                  className="rounded-md bg-amber-500/15 p-1.5 gold-text hover:bg-amber-500/30 disabled:opacity-50"
                  data-testid={`admin-pause-user-${user.id}`}
                >
                  <PauseCircle className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                onClick={() => run(() => onDelete(user.id, user.username))}
                disabled={busy}
                title="Permanently delete user"
                className="rounded-md bg-red-500/15 p-1.5 text-red-300 hover:bg-red-500/30 disabled:opacity-50"
                data-testid={`admin-delete-user-${user.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        {(() => {
          const isVip = user.creator_status === 'active';
          const monet = user.monetization_enabled !== false;
          let badge = { text: 'Not Creator', color: 'bg-muted text-muted-foreground', Icon: XCircleIcon };
          if (isVip && monet) badge = { text: 'Voce Pro · Attiva', color: 'bg-emerald-500/20 text-emerald-300', Icon: Crown };
          else if (isVip && !monet) badge = { text: 'Suspended', color: 'bg-red-500/20 text-red-300', Icon: Ban };
          else if (user.stripe_connect_id) badge = { text: 'In verification', color: 'bg-amber-500/20 gold-text', Icon: Crown };
          const Icon = badge.Icon;
          return (
            <div className="flex flex-wrap items-center gap-1.5">
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${badge.color}`}
                data-testid={`user-vip-badge-${user.id}`}
              >
                <Icon className="h-3 w-3" /> {badge.text}
              </span>
              {!user.is_admin && isVip && (
                monet ? (
                  <button
                    onClick={() => run(() => onSuspendMonet(user.id, user.username))}
                    disabled={busy}
                    title="Suspend monetization (paid posts, paid lives, tips)"
                    className="rounded-md bg-red-500/15 p-1.5 text-red-300 hover:bg-red-500/30 disabled:opacity-50"
                    data-testid={`admin-suspend-monet-${user.id}`}
                  >
                    <Ban className="h-3.5 w-3.5" />
                  </button>
                ) : (
                  <button
                    onClick={() => run(() => onResumeMonet(user.id))}
                    disabled={busy}
                    title="Resume monetization"
                    className="rounded-md bg-emerald-500/15 p-1.5 text-emerald-300 hover:bg-emerald-500/30 disabled:opacity-50"
                    data-testid={`admin-resume-monet-${user.id}`}
                  >
                    <Crown className="h-3.5 w-3.5" />
                  </button>
                )
              )}
            </div>
          );
        })()}
      </td>
    </tr>
  );
};

// Helper icon component used in the conditional badge above
const XCircleIcon = (props) => <X {...props} />;

// Defined at module scope (NOT inside SettingsSection) so React keeps a stable
// identity for these components — otherwise <Input> would unmount/remount on
// every keystroke and lose focus, making the field appear "read-only".
const SettingsSec = ({ children, title, icon: Icon }) => (
  <div className="rounded-xl border border-border bg-card p-4">
    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold gold-text">
      <Icon className="h-4 w-4" /> {title}
    </h3>
    <div className="space-y-3">{children}</div>
  </div>
);

const SettingsField = ({ label, hint, envLocked, ...props }) => (
  <div>
    <div className="mb-1 flex items-center gap-2">
      <label className="block text-xs font-semibold text-foreground">{label}</label>
      {envLocked && (
        <span
          className="inline-flex items-center gap-1 rounded-sm border border-blue-500/30 bg-blue-500/10 px-1.5 py-0.5 text-[10px] font-medium text-blue-700"
          data-testid={`env-lock-badge-${(props.name || label || '').toString().toLowerCase().replace(/\s+/g, '-')}`}
        >
          <Lock className="h-2.5 w-2.5" />
          Managed by Environment
        </span>
      )}
    </div>
    <Input
      {...props}
      disabled={!!envLocked || props.disabled}
      className={`h-9 bg-background ${envLocked ? 'cursor-not-allowed opacity-60' : ''}`}
    />
    {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
  </div>
);

const IapAdminToggleRow = ({ data, onSave }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const enabled = !!data?.iap_enabled;
  const masterAllowed = !!data?.iap_master_allowed;
  const effective = !!data?.iap_effective;

  const persist = async (value) => {
    setBusy(true);
    try { await onSave({ iap_enabled: value }); } finally { setBusy(false); }
  };

  const onToggle = (v) => {
    if (!masterAllowed) return;
    if (v) setConfirmOpen(true); else persist(false);
  };

  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2" data-testid="iap-admin-toggle-row">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold">Acquisti crediti da App</p>
          <span
            className={`rounded-sm px-1.5 py-0.5 text-[10px] font-semibold ${effective ? 'bg-emerald-500/20 text-emerald-600' : 'bg-secondary text-muted-foreground'}`}
            data-testid="iap-admin-toggle-state"
          >
            {enabled ? 'ON' : 'OFF'}
          </span>
          {!masterAllowed && (
            <span
              className="inline-flex items-center gap-1 rounded-sm border border-red-500/30 bg-red-500/10 px-1.5 py-0.5 text-[10px] font-medium text-red-600"
              data-testid="iap-master-blocked-badge"
            >
              <Lock className="h-2.5 w-2.5" /> Bloccato dalla configurazione server
            </span>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground">
          Abilita gli acquisti reali di crediti tramite Apple App Store e Google Play.
        </p>
      </div>
      <Switch
        checked={enabled}
        disabled={!masterAllowed || busy}
        onCheckedChange={onToggle}
        data-testid="iap-admin-toggle"
      />
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="iap-enable-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Attivare gli acquisti reali in-app?</AlertDialogTitle>
            <AlertDialogDescription>
              Gli utenti potranno acquistare crediti tramite gli Store se la configurazione server è abilitata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="iap-enable-cancel-btn">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={() => persist(true)} data-testid="iap-enable-confirm-btn">Attiva</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const SettingsSection = ({ data, onSave }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    stripe_secret_key: '',
    stripe_publishable_key: data?.stripe_publishable_key || '',
    stripe_webhook_secret: '',
    stripe_connect_webhook_secret: '',
    stripe_connect_client_id: data?.stripe_connect_client_id || '',
    stripe_mock_mode: !!data?.stripe_mock_mode,
    // Phase 37A.1 — Stripe Payments webhook secret + top-up v2 flag
    stripe_payments_webhook_secret: '',
    wallet_topup_v2: !!data?.wallet_topup_v2,
    stripe_wallets_enabled: !!data?.stripe_wallets_enabled,
    google_login_enabled: !!data?.google_login_enabled,
    people_feed_enabled: !!data?.people_feed_enabled,
    // Phase F3 — Voice-First video kill-switches
    video_posts_enabled: !!data?.video_posts_enabled,
    video_live_enabled: !!data?.video_live_enabled,
    video_o2o_enabled: !!data?.video_o2o_enabled,
    facebook_login_enabled: !!data?.facebook_login_enabled,
    apple_login_enabled: !!data?.apple_login_enabled,
    facebook_app_id: data?.facebook_app_id || '',
    facebook_app_secret: '',
    // LiveKit Cloud (Phase 24)
    livekit_url: data?.livekit_url || '',
    livekit_api_key: '',
    livekit_api_secret: '',
    livekit_mock_mode: !!data?.livekit_mock_mode,
    smtp_host: data?.smtp_host || '',
    smtp_port: data?.smtp_port || 587,
    smtp_username: data?.smtp_username || '',
    smtp_password: '',
    smtp_from_email: data?.smtp_from_email || '',
    smtp_from_name: data?.smtp_from_name || 'Nymia',
    smtp_use_tls: data?.smtp_use_tls ?? true,
    smtp_mock_mode: !!data?.smtp_mock_mode,
    // Phase 2D — Resend
    resend_api_key: '',
    resend_from_email: data?.resend_from_email || '',
    resend_from_name: data?.resend_from_name || 'Nymia',
    // Phase 95 — SMS / OTP gateway
    sms_provider: data?.sms_provider || 'mock',
    sms_api_key: '',
    sms_api_secret: '',
    sms_from: data?.sms_from || 'Nymia',
    wasabi_access_key: '',
    wasabi_secret_key: '',
    wasabi_bucket_name: data?.wasabi_bucket_name || '',
    wasabi_region: data?.wasabi_region || 'us-east-1',
    wasabi_endpoint: data?.wasabi_endpoint || '',
    wasabi_public_base_url: data?.wasabi_public_base_url || '',
    storage_mock_mode: !!data?.storage_mock_mode,
    meta_pixel_id: data?.meta_pixel_id || '',
    meta_access_token: '',
    meta_test_event_code: data?.meta_test_event_code || '',
    meta_mock_mode: !!data?.meta_mock_mode,
    sightengine_enabled: !!data?.sightengine_enabled,
    sightengine_api_user: data?.sightengine_api_user || '',
    sightengine_api_secret: '',
    require_paid_post_approval: !!data?.require_paid_post_approval,
    require_creator_approval: !!data?.require_creator_approval,
    live_broadcast_emails_enabled: !!data?.live_broadcast_emails_enabled,
    notify_all_voce_pro: data?.notify_all_voce_pro !== false,
    notify_all_users: data?.notify_all_users !== false,
    vp_message_emails_enabled: data?.vp_message_emails_enabled !== false,
    legal_footer: data?.legal_footer || '',
  });
  const [saving, setSaving] = useState(false);
  const [smtpTestEmail, setSmtpTestEmail] = useState('');
  const [smtpTesting, setSmtpTesting] = useState(false);
  // Phase 2D — Resend diagnostics
  const [resendTestEmail, setResendTestEmail] = useState('');
  const [resendTesting, setResendTesting] = useState(false);
  const [resendTestResult, setResendTestResult] = useState(null);
  // Phase 95 — SMS / OTP gateway diagnostics
  const [smsTestPhone, setSmsTestPhone] = useState('');
  const [smsTesting, setSmsTesting] = useState(false);
  const [smsTestResult, setSmsTestResult] = useState(null);
  // Phase 81.2 — broadcast preview email
  const [broadcastPreviewSending, setBroadcastPreviewSending] = useState(false);

  const handleBroadcastPreview = async () => {
    setBroadcastPreviewSending(true);
    try {
      const res = await adminLiveBroadcastPreview();
      const mock = (res.results || []).some((r) => r.mock);
      toast({
        title: mock ? 'Anteprime registrate (mock mode)' : 'Anteprime inviate',
        description: `3 email di esempio (Live Ora, Programmata, Chiamate) a ${res.to}`,
      });
    } catch (e) {
      toast({
        title: 'Invio anteprima fallito',
        description: e.response?.data?.detail || 'Riprova',
        variant: 'destructive',
      });
    } finally {
      setBroadcastPreviewSending(false);
    }
  };

  // Phase 27.1.5 — per-section save state. After each save the backend
  // returns {applied_fields, ignored_fields}; we render a tiny summary
  // strip beneath each section button.
  const [lastResult, setLastResult] = useState({});

  const saveSection = async (section, payload) => {
    setSaving(true);
    try {
      const res = await onSave(payload);
      const r = (res && typeof res === 'object') ? res : { applied_fields: Object.keys(payload), ignored_fields: [] };
      setLastResult((prev) => ({
        ...prev,
        [section]: {
          applied: r.applied_fields || Object.keys(payload),
          ignored: r.ignored_fields || [],
          at: new Date().toISOString(),
        },
      }));
      toast({
        title: `${section} settings saved`,
        description: (r.ignored_fields && r.ignored_fields.length > 0)
          ? `${(r.applied_fields || []).length} applied · ${r.ignored_fields.length} ignored (env-locked)`
          : `${(r.applied_fields || []).length} fields applied`,
      });
    } catch (e) {
      toast({
        title: `Save ${section} failed`,
        description: e?.response?.data?.detail || e?.message || 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Phase 27.1.5 — per-section payload builders. Each one strips secrets
  // if empty (so we never overwrite a saved hash with "").
  const handleSaveWasabi = () => {
    const p = {
      wasabi_bucket_name: form.wasabi_bucket_name,
      wasabi_region: form.wasabi_region,
      wasabi_endpoint: form.wasabi_endpoint,
      wasabi_public_base_url: form.wasabi_public_base_url,
      storage_mock_mode: form.storage_mock_mode,
    };
    if (form.wasabi_access_key) p.wasabi_access_key = form.wasabi_access_key;
    if (form.wasabi_secret_key) p.wasabi_secret_key = form.wasabi_secret_key;
    return saveSection('Wasabi', p);
  };

  const handleSaveLiveKit = () => {
    const p = {
      livekit_url: form.livekit_url,
      livekit_mock_mode: form.livekit_mock_mode,
    };
    if (form.livekit_api_key) p.livekit_api_key = form.livekit_api_key;
    if (form.livekit_api_secret) p.livekit_api_secret = form.livekit_api_secret;
    return saveSection('LiveKit', p);
  };

  const handleSaveStripe = () => {
    const p = {
      stripe_publishable_key: form.stripe_publishable_key,
      stripe_connect_client_id: form.stripe_connect_client_id,
      stripe_mock_mode: form.stripe_mock_mode,
      // Phase 2D — expose wallet_topup_v2 in Stripe save so admin can flip it.
      wallet_topup_v2: form.wallet_topup_v2,
      stripe_wallets_enabled: form.stripe_wallets_enabled,
    };
    if (form.stripe_secret_key) p.stripe_secret_key = form.stripe_secret_key;
    if (form.stripe_webhook_secret) p.stripe_webhook_secret = form.stripe_webhook_secret;
    if (form.stripe_payments_webhook_secret) p.stripe_payments_webhook_secret = form.stripe_payments_webhook_secret;
    if (form.stripe_connect_webhook_secret) p.stripe_connect_webhook_secret = form.stripe_connect_webhook_secret;
    return saveSection('Stripe', p);
  };

  const handleSaveSmtp = () => {
    const p = {
      smtp_host: form.smtp_host,
      smtp_port: parseInt(form.smtp_port, 10) || 587,
      smtp_username: form.smtp_username,
      smtp_from_email: form.smtp_from_email,
      smtp_from_name: form.smtp_from_name,
      smtp_use_tls: form.smtp_use_tls,
      smtp_mock_mode: form.smtp_mock_mode,
    };
    if (form.smtp_password) p.smtp_password = form.smtp_password;
    return saveSection('SMTP', p);
  };

  // Phase 2D — Resend section saver. Empty api_key is stripped so we
  // never overwrite the stored value.
  // Phase 68 — Social login section saver. Empty secret is stripped so we
  // never overwrite the stored Facebook App Secret.
  const handleSaveSocial = () => {
    const p = {
      google_login_enabled: form.google_login_enabled,
      facebook_login_enabled: form.facebook_login_enabled,
      apple_login_enabled: form.apple_login_enabled,
      facebook_app_id: form.facebook_app_id,
    };
    if (form.facebook_app_secret) p.facebook_app_secret = form.facebook_app_secret;
    return saveSection('Social', p);
  };

  const handleSavePeopleFeed = () =>
    saveSection('Feed Persone', { people_feed_enabled: form.people_feed_enabled });

  // Phase F3 — Voice-First video kill-switches (independent toggles, one save)
  const handleSaveVideoFeatures = () =>
    saveSection('Funzioni Video', {
      video_posts_enabled: form.video_posts_enabled,
      video_live_enabled: form.video_live_enabled,
      video_o2o_enabled: form.video_o2o_enabled,
    });

  const handleSaveResend = () => {
    const p = {
      resend_from_email: form.resend_from_email,
      resend_from_name: form.resend_from_name,
    };
    if (form.resend_api_key) p.resend_api_key = form.resend_api_key;
    return saveSection('Resend', p);
  };

  // Phase 95 — SMS / OTP gateway. Empty secrets are stripped (keep existing);
  // provider/sender are sent only when actually changed by the admin.
  const handleSaveSms = () => {
    const p = {};
    if (form.sms_provider !== (data?.sms_provider || 'mock')) p.sms_provider = form.sms_provider;
    if (form.sms_from !== (data?.sms_from || 'Nymia')) p.sms_from = form.sms_from;
    if (form.sms_api_key) p.sms_api_key = form.sms_api_key;
    if (form.sms_api_secret) p.sms_api_secret = form.sms_api_secret;
    if (Object.keys(p).length === 0) {
      toast({ title: 'Nessuna modifica SMS da salvare' });
      return undefined;
    }
    return saveSection('SMS', p);
  };

  const handleSmsTest = async () => {
    if (!smsTestPhone.trim()) {
      toast({ title: 'Inserisci un numero di telefono', variant: 'destructive' });
      return;
    }
    setSmsTesting(true);
    setSmsTestResult(null);
    try {
      const res = await adminSmsTest(smsTestPhone.trim());
      setSmsTestResult(res);
      if (res.simulated) {
        toast({ title: 'Test SIMULATO (provider mock)', description: 'Nessun SMS reale inviato.' });
      } else if (res.sent) {
        toast({ title: 'SMS di test inviato', description: `via ${res.provider}` });
      } else {
        toast({ title: 'Test SMS fallito', description: res.error || '—', variant: 'destructive' });
      }
    } catch (e) {
      const detail = String(e.response?.data?.detail || 'Errore');
      setSmsTestResult({ sent: false, simulated: false, error: detail.includes('|') ? detail.split('|')[1] : detail });
      toast({ title: 'Test SMS fallito', description: detail.includes('|') ? detail.split('|')[1] : detail, variant: 'destructive' });
    } finally {
      setSmsTesting(false);
    }
  };

  const handleSaveGeneral = () => {
    const p = {
      meta_pixel_id: form.meta_pixel_id,
      meta_test_event_code: form.meta_test_event_code,
      meta_mock_mode: form.meta_mock_mode,
      sightengine_enabled: form.sightengine_enabled,
      sightengine_api_user: form.sightengine_api_user,
      require_paid_post_approval: form.require_paid_post_approval,
      require_creator_approval: form.require_creator_approval,
      legal_footer: form.legal_footer,
    };
    if (form.meta_access_token) p.meta_access_token = form.meta_access_token;
    if (form.sightengine_api_secret) p.sightengine_api_secret = form.sightengine_api_secret;
    return saveSection('General', p);
  };

  // Phase 84.1 — dedicated save for the Sightengine section (before this fix
  // its fields were only persisted by the far-away "Save General" button).
  const handleSaveSightengine = () => {
    const p = {
      sightengine_enabled: form.sightengine_enabled,
      sightengine_api_user: form.sightengine_api_user,
    };
    if (form.sightengine_api_secret) p.sightengine_api_secret = form.sightengine_api_secret;
    return saveSection('Sightengine', p);
  };

  // Phase 27.1.5 — tiny inline component showing applied/ignored/timestamp.
  // eslint-disable-next-line react/no-unstable-nested-components
  const SaveSummary = ({ section }) => {
    const r = lastResult[section];
    if (!r) return null;
    const ts = new Date(r.at).toLocaleTimeString();
    return (
      <div className="mt-1 rounded bg-background/50 px-2 py-1 text-[10px] text-muted-foreground" data-testid={`save-summary-${section.toLowerCase()}`}>
        <span className="font-mono">{ts}</span> ·{' '}
        <span className="text-emerald-700">✅ {r.applied.length} applied</span>
        {r.applied.length > 0 && <span className="text-emerald-700/70"> ({r.applied.slice(0, 4).join(', ')}{r.applied.length > 4 ? '…' : ''})</span>}
        {r.ignored.length > 0 && (
          <>
            {' · '}
            <span className="text-amber-700">⚠️ {r.ignored.length} ignored</span>
            <span className="text-amber-700/70"> ({r.ignored.join(', ')})</span>
          </>
        )}
      </div>
    );
  };

  // Phase 27.1.5 — legacy bulk save removed in favour of per-section buttons.

  // Phase 27.1.4 — per-section save buttons. Each one sends ONLY the fields
  // of its own section so a 409/400 in one section (e.g. LiveKit env-locked)
  // never blocks another (e.g. Wasabi).
  // (Implementation moved above to Phase 27.1.5 with summary support.)

  const handleSmtpTest = async () => {
    if (!smtpTestEmail) {
      toast({ title: 'Enter a destination email first', variant: 'destructive' });
      return;
    }
    setSmtpTesting(true);
    try {
      const res = await adminSmtpTest(smtpTestEmail);
      if (res.sent) {
        toast({
          title: res.mock ? 'SMTP test logged (mock mode)' : 'SMTP test sent',
          description: `to ${smtpTestEmail}`,
        });
      } else {
        toast({
          title: 'SMTP test failed',
          description: res.error || 'Check server logs',
          variant: 'destructive',
        });
      }
    } catch (e) {
      toast({
        title: 'SMTP test failed',
        description: e.response?.data?.detail || 'Try again',
        variant: 'destructive',
      });
    } finally {
      setSmtpTesting(false);
    }
  };

  // Phase 2D — Resend end-to-end test. Calls POST /admin/settings/resend-test
  // which returns { sent, provider, id?, error?, diagnostics: {…} } so the
  // admin sees exactly WHICH transport was used (resend / smtp / mock) and
  // WHY (env vs db config, api key source).
  const handleResendTest = async () => {
    if (!resendTestEmail) {
      toast({ title: 'Inserisci una email di destinazione', variant: 'destructive' });
      return;
    }
    setResendTesting(true);
    setResendTestResult(null);
    try {
      const tok = localStorage.getItem('ig_token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/resend-test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ to_email: resendTestEmail }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
      setResendTestResult(j);
      if (j.sent && !j.mock) {
        toast({
          title: `✅ Email inviata via ${j.provider}`,
          description: j.provider === 'resend'
            ? `Resend id: ${j.id || '—'} · destinatario: ${resendTestEmail}`
            : `Fallback ${j.provider} · destinatario: ${resendTestEmail}`,
        });
      } else if (j.mock) {
        toast({
          title: '⚠️ Modalità mock',
          description: 'Nessun provider configurato — email loggata solo in DB (Audit → Email logs).',
          variant: 'destructive',
        });
      } else {
        toast({
          title: '❌ Invio fallito',
          description: j.error || 'Vedi log backend',
          variant: 'destructive',
        });
      }
    } catch (e) {
      const msg = e?.message || String(e);
      setResendTestResult({ error: msg });
      toast({ title: 'Test Resend fallito', description: msg, variant: 'destructive' });
    } finally {
      setResendTesting(false);
    }
  };

  const Sec = SettingsSec;
  const Field = SettingsField;

  return (
    <div className="space-y-4">
      <Sec title="Stripe (Payments)" icon={KeyRound}>
        {/* Phase 2D — Live-mode readiness diagnostic. Answers
            "why is Stripe still in mock/test?" without exposing raw keys. */}
        <StripeReadinessCard />
        <div className="flex items-center justify-between rounded-md border border-border bg-background/40 px-3 py-2">
          <div>
            <p className="text-sm font-semibold">Mock mode</p>
            <p className="text-[11px] text-muted-foreground">When on, payments are simulated. Turn off when real keys are entered.</p>
          </div>
          <Switch checked={form.stripe_mock_mode} onCheckedChange={(v) => setForm({ ...form, stripe_mock_mode: v })} />
        </div>
        <Field
          label="Secret key (sk_live_… or sk_test_…)"
          placeholder={data?.stripe_secret_key_masked || 'sk_test_...'}
          value={form.stripe_secret_key}
          onChange={(e) => setForm({ ...form, stripe_secret_key: e.target.value })}
          hint="Used for both Checkout and Connect Express. Leave blank to keep existing value."
        />
        <Field
          label="Publishable key (pk_live_… / pk_test_…)"
          value={form.stripe_publishable_key}
          onChange={(e) => setForm({ ...form, stripe_publishable_key: e.target.value })}
        />
        <Field
          label="Checkout webhook secret (legacy)"
          placeholder={data?.stripe_webhook_secret_masked || 'whsec_...'}
          value={form.stripe_webhook_secret}
          onChange={(e) => setForm({ ...form, stripe_webhook_secret: e.target.value })}
          hint="Leave blank to keep existing value"
        />
        <Field
          label="Payments webhook secret (v2 · checkout.session.completed)"
          placeholder={data?.stripe_payments_webhook_secret_masked || 'whsec_...'}
          value={form.stripe_payments_webhook_secret}
          onChange={(e) => setForm({ ...form, stripe_payments_webhook_secret: e.target.value })}
          hint="Signing secret for /api/webhook/stripe-payments — richiesto se wallet_topup_v2 è ON."
        />
        <Field
          label="Connect webhook secret"
          placeholder={data?.stripe_connect_webhook_secret_masked || 'whsec_...'}
          value={form.stripe_connect_webhook_secret}
          onChange={(e) => setForm({ ...form, stripe_connect_webhook_secret: e.target.value })}
          hint="Separate endpoint signing secret for /api/webhook/stripe-connect"
        />
        <Field
          label="Stripe Connect client ID"
          value={form.stripe_connect_client_id}
          onChange={(e) => setForm({ ...form, stripe_connect_client_id: e.target.value })}
          hint="ca_... (used by Stripe Connect Express. Identity verification handled natively in the onboarding flow.)"
        />
        {/* Phase 2D — wallet_topup_v2 toggle. When ON the frontend calls
            POST /api/wallet/topup/checkout which issues a real Stripe
            Checkout session; when OFF it falls back to the legacy mock
            /shop/topup + /checkout/:id path ("simulated checkout" page). */}
        <div className="flex items-center justify-between rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2" data-testid="wallet-topup-v2-row">
          <div>
            <p className="text-sm font-semibold">Wallet top-up v2 (real Stripe Checkout)</p>
            <p className="text-[11px] text-muted-foreground">
              ON = i top-up passano dal vero Stripe Checkout hosted (<code className="rounded bg-secondary px-1">/wallet/topup/checkout</code>).
              OFF = flow legacy mock (<code className="rounded bg-secondary px-1">/shop/topup</code>) — mostra la finta pagina &quot;simulated checkout&quot;.
              Per LIVE deve essere <strong>ON</strong>.
            </p>
          </div>
          <Switch
            checked={form.wallet_topup_v2}
            onCheckedChange={(v) => setForm({ ...form, wallet_topup_v2: v })}
            data-testid="wallet-topup-v2-toggle"
          />
        </div>
        {/* Apple Pay / Google Pay wallets toggle. ON = Stripe Checkout usa i
            Dynamic Payment Methods e mostra automaticamente Apple Pay e
            Google Pay sui dispositivi compatibili. OFF = solo carta. */}
        <div className="flex items-center justify-between rounded-md border border-sky-500/30 bg-sky-500/5 px-3 py-2" data-testid="stripe-wallets-row">
          <div>
            <p className="text-sm font-semibold">Apple Pay / Google Pay</p>
            <p className="text-[11px] text-muted-foreground">
              ON = il checkout Stripe mostra automaticamente Apple Pay e Google Pay
              (oltre alla carta) sui dispositivi compatibili. I wallet devono essere
              attivi anche su Stripe Dashboard → Settings → Payment methods.
              OFF = solo pagamento con carta.
            </p>
          </div>
          <Switch
            checked={form.stripe_wallets_enabled}
            onCheckedChange={(v) => setForm({ ...form, stripe_wallets_enabled: v })}
            data-testid="stripe-wallets-toggle"
          />
        </div>
        {/* F9 — admin toggle for real in-app credit purchases (saved immediately,
            effective only when the env master IAP_MASTER_ALLOWED is true). */}
        <IapAdminToggleRow data={data} onSave={(p) => saveSection('Acquisti da App', p)} />
        {/* Phase 27.1.5 — per-section save. Sends only stripe_* fields so a
            problem in LiveKit/Wasabi can never block Stripe updates. */}
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            onClick={handleSaveStripe}
            disabled={saving}
            size="sm"
            className="gold-gradient text-black"
            data-testid="save-stripe-btn"
          >
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
            {saving ? 'Salvataggio…' : 'Save Stripe'}
          </Button>
        </div>
        <SaveSummary section="Stripe" />
      </Sec>

      {/* Phase F1 — Voice-First People Feed flag */}
      <Sec title="Feed Persone (Voice-First)" icon={Users}>
        <div className="flex items-center justify-between rounded-md border border-border bg-background/40 px-3 py-2" data-testid="people-feed-row">
          <div>
            <p className="text-sm font-semibold">Feed Persone</p>
            <p className="text-[11px] text-muted-foreground">
              ON = la Home mostra il nuovo Feed di Persone (business card anonime voice-first).
              OFF = resta il feed classico. Rollback immediato spegnendo il toggle.
            </p>
          </div>
          <Switch
            checked={form.people_feed_enabled}
            onCheckedChange={(v) => setForm({ ...form, people_feed_enabled: v })}
            data-testid="people-feed-toggle"
          />
        </div>
        <Button size="sm" onClick={handleSavePeopleFeed} data-testid="save-people-feed-btn">
          Salva Feed Persone
        </Button>
      </Sec>

      {/* Phase F3 — Voice-First: admin kill-switches for NEW video creation.
          OFF = the video feature disappears from the UI and is blocked by the
          backend. Legacy videos, DB records and moderation stay untouched. */}
      <Sec title="Funzioni Video (Voice-First)" icon={FileVideo}>
        <p className="text-[11px] text-muted-foreground">
          OFF = la funzione video sparisce dalla UI ed è bloccata anche dal backend.
          I video già pubblicati, i dati esistenti e la moderazione NON vengono toccati.
          Audio, Live Audio e Chiamate Audio restano sempre disponibili.
        </p>
        <div className="flex items-center justify-between rounded-md border border-border bg-background/40 px-3 py-2" data-testid="video-posts-row">
          <div>
            <p className="text-sm font-semibold">Post Video</p>
            <p className="text-[11px] text-muted-foreground">
              ON = consente la pubblicazione di nuovi post video nel feed.
              OFF = l&apos;upload video sparisce dalla pagina Crea.
            </p>
          </div>
          <Switch
            checked={form.video_posts_enabled}
            onCheckedChange={(v) => setForm({ ...form, video_posts_enabled: v })}
            data-testid="video-posts-toggle"
          />
        </div>
        <div className="flex items-center justify-between rounded-md border border-border bg-background/40 px-3 py-2" data-testid="video-live-row">
          <div>
            <p className="text-sm font-semibold">Live Video</p>
            <p className="text-[11px] text-muted-foreground">
              ON = consente la creazione e l&apos;avvio di Live Video.
              OFF = restano disponibili solo le Live Audio.
            </p>
          </div>
          <Switch
            checked={form.video_live_enabled}
            onCheckedChange={(v) => setForm({ ...form, video_live_enabled: v })}
            data-testid="video-live-toggle"
          />
        </div>
        <div className="flex items-center justify-between rounded-md border border-border bg-background/40 px-3 py-2" data-testid="video-o2o-row">
          <div>
            <p className="text-sm font-semibold">One-to-One Video</p>
            <p className="text-[11px] text-muted-foreground">
              ON = le Voce Pro possono offrire chiamate video.
              OFF = le chiamate restano solo audio.
            </p>
          </div>
          <Switch
            checked={form.video_o2o_enabled}
            onCheckedChange={(v) => setForm({ ...form, video_o2o_enabled: v })}
            data-testid="video-o2o-toggle"
          />
        </div>
        <Button size="sm" onClick={handleSaveVideoFeatures} data-testid="save-video-features-btn">
          Salva Funzioni Video
        </Button>
      </Sec>

      {/* Phase 68 — Social login (Google via Emergent Auth + Facebook OAuth).
          Email/password login always stays available. */}
      <Sec title="Login social (Google / Facebook)" icon={KeyRound}>
        <div className="flex items-center justify-between rounded-md border border-border bg-background/40 px-3 py-2" data-testid="google-login-row">
          <div>
            <p className="text-sm font-semibold">Login con Google</p>
            <p className="text-[11px] text-muted-foreground">
              ON = sulla pagina di accesso e registrazione appare il pulsante &quot;Continua con Google&quot;.
              Nessuna chiave richiesta (gestito da Emergent Auth). Il login con email resta sempre disponibile.
            </p>
          </div>
          <Switch
            checked={form.google_login_enabled}
            onCheckedChange={(v) => setForm({ ...form, google_login_enabled: v })}
            data-testid="google-login-toggle"
          />
        </div>
        <div className="flex items-center justify-between rounded-md border border-border bg-background/40 px-3 py-2" data-testid="facebook-login-row">
          <div>
            <p className="text-sm font-semibold">Login con Facebook</p>
            <p className="text-[11px] text-muted-foreground">
              ON = appare il pulsante &quot;Continua con Facebook&quot;. Richiede App ID e App Secret
              (Meta Developers → la tua app → Impostazioni di base). Il pulsante compare solo se le chiavi sono salvate.
            </p>
          </div>
          <Switch
            checked={form.facebook_login_enabled}
            onCheckedChange={(v) => setForm({ ...form, facebook_login_enabled: v })}
            data-testid="facebook-login-toggle"
          />
        </div>
        <div className="flex items-center justify-between rounded-md border border-border bg-background/40 px-3 py-2" data-testid="apple-login-row">
          <div>
            <p className="text-sm font-semibold">Login con Apple (solo app iOS)</p>
            <p className="text-[11px] text-muted-foreground">
              ON = la Neo App iOS può usare &quot;Accedi con Apple&quot; (endpoint nativo). Nessun pulsante sul sito web.
              Richiede APPLE_BUNDLE_ID configurato sul server. Google ed email restano invariati.
            </p>
          </div>
          <Switch
            checked={form.apple_login_enabled}
            onCheckedChange={(v) => setForm({ ...form, apple_login_enabled: v })}
            data-testid="apple-login-toggle"
          />
        </div>
        <Field
          label="Facebook App ID"
          value={form.facebook_app_id}
          onChange={(e) => setForm({ ...form, facebook_app_id: e.target.value })}
          hint="Meta Developers → App → Impostazioni di base"
          name="facebook-app-id"
        />
        <Field
          label="Facebook App Secret"
          type="password"
          placeholder={data?.facebook_app_secret_masked || ''}
          value={form.facebook_app_secret}
          onChange={(e) => setForm({ ...form, facebook_app_secret: e.target.value })}
          hint="Lascia vuoto per mantenere il valore salvato"
          name="facebook-app-secret"
        />
        <div className="rounded-md border border-sky-500/30 bg-sky-500/5 px-3 py-2 text-[11px] text-muted-foreground">
          ⚠️ Su Meta Developers → Facebook Login → Impostazioni, aggiungi questo URI esatto tra i
          &quot;Valid OAuth Redirect URIs&quot;:{' '}
          <code className="font-mono text-sky-600">{`${window.location.origin}/api/auth/facebook/callback`}</code>
          {' '}(e l&apos;equivalente del dominio di produzione).
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            onClick={handleSaveSocial}
            disabled={saving}
            size="sm"
            className="gold-gradient text-black"
            data-testid="save-social-login-btn"
          >
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
            {saving ? 'Salvataggio…' : 'Save Login social'}
          </Button>
        </div>
        <SaveSummary section="Social" />
      </Sec>

      <Sec title="LiveKit Cloud (Realtime streaming engine)" icon={Radio}>
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-[11px] text-emerald-700">
          ✅ Active provider for new lives. Powers video, audio, co-host, chiamate and future replay/recording.
        </div>
        {data?.livekit_has_env_override && (
          <div className="rounded-md border border-blue-500/30 bg-blue-500/5 px-3 py-2 text-[11px] text-blue-700">
            🔒 Credentials are managed by environment variables (LIVEKIT_URL, LIVEKIT_API_KEY, LIVEKIT_API_SECRET). They are <strong>immune to redeploys and Admin UI writes</strong>. To change them, update the deployment env vars.
          </div>
        )}
        <div className="flex items-center justify-between rounded-md border border-border bg-background/40 px-3 py-2">
          <div>
            <p className="text-sm font-semibold">Mock mode</p>
            <p className="text-[11px] text-muted-foreground">Off in production. When on, no real LiveKit calls are made — useful for staging/dev.</p>
          </div>
          <Switch
            data-testid="livekit-mock-toggle"
            checked={form.livekit_mock_mode}
            onCheckedChange={(v) => setForm({ ...form, livekit_mock_mode: v })}
          />
        </div>
        <Field
          label="LiveKit URL"
          value={form.livekit_url}
          onChange={(e) => setForm({ ...form, livekit_url: e.target.value })}
          hint="wss://<your-project>.livekit.cloud"
          envLocked={!!data?.livekit_has_env_override}
          name="livekit-url"
        />
        <Field
          label="LiveKit API key"
          placeholder={data?.livekit_api_key_masked || ''}
          value={form.livekit_api_key}
          onChange={(e) => setForm({ ...form, livekit_api_key: e.target.value })}
          hint="Leave blank to keep existing value"
          envLocked={!!data?.livekit_has_env_override}
          name="livekit-api-key"
        />
        <Field
          label="LiveKit API secret"
          placeholder={data?.livekit_api_secret_masked || ''}
          value={form.livekit_api_secret}
          onChange={(e) => setForm({ ...form, livekit_api_secret: e.target.value })}
          hint="Leave blank to keep existing value. Server-side only — never sent to the browser."
          envLocked={!!data?.livekit_has_env_override}
          name="livekit-api-secret"
        />
        <LiveKitDiagnostics />
        {/* Phase 27.1.5 — per-section save. Sends only livekit_* fields.
            If env-vars are authoritative the backend silently strips them
            and reports them as ignored_fields in the summary below. */}
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            onClick={handleSaveLiveKit}
            disabled={saving}
            size="sm"
            className="gold-gradient text-black"
            data-testid="save-livekit-btn"
          >
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
            {saving ? 'Salvataggio…' : 'Save LiveKit'}
          </Button>
        </div>
        <SaveSummary section="LiveKit" />
      </Sec>

      <Sec title="Email (SMTP)" icon={Mail}>
        <div className="flex items-center justify-between rounded-md border border-border bg-background/40 px-3 py-2">
          <div>
            <p className="text-sm font-semibold">Mock mode</p>
            <p className="text-[11px] text-muted-foreground">When on, transactional emails are logged but not sent.</p>
          </div>
          <Switch
            checked={form.smtp_mock_mode}
            onCheckedChange={(v) => setForm({ ...form, smtp_mock_mode: v })}
            data-testid="smtp-mock-toggle"
          />
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field
            label="SMTP server"
            value={form.smtp_host}
            onChange={(e) => setForm({ ...form, smtp_host: e.target.value })}
            placeholder="smtp.example.com"
          />
          <Field
            label="Port"
            type="number"
            value={form.smtp_port}
            onChange={(e) => setForm({ ...form, smtp_port: e.target.value })}
            placeholder="587"
          />
          <Field
            label="Username"
            value={form.smtp_username}
            onChange={(e) => setForm({ ...form, smtp_username: e.target.value })}
            placeholder="apikey or login"
          />
          <Field
            label="Password"
            type="password"
            placeholder={data?.smtp_password_masked || '••••••••'}
            value={form.smtp_password}
            onChange={(e) => setForm({ ...form, smtp_password: e.target.value })}
            hint="Leave blank to keep existing value"
          />
          <Field
            label="From email"
            value={form.smtp_from_email}
            onChange={(e) => setForm({ ...form, smtp_from_email: e.target.value })}
            placeholder="no-reply@nymia.app"
          />
          <Field
            label="From name"
            value={form.smtp_from_name}
            onChange={(e) => setForm({ ...form, smtp_from_name: e.target.value })}
            placeholder="Nymia"
          />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold">Use STARTTLS</p>
            <p className="text-[11px] text-muted-foreground">Recommended for port 587. Port 465 uses implicit TLS automatically.</p>
          </div>
          <Switch checked={form.smtp_use_tls} onCheckedChange={(v) => setForm({ ...form, smtp_use_tls: v })} />
        </div>
        <div className="rounded-md border border-border bg-background/40 p-3">
          <p className="mb-2 text-xs font-semibold text-foreground">Test SMTP</p>
          <div className="flex gap-2">
            <Input
              placeholder="Destination email"
              value={smtpTestEmail}
              onChange={(e) => setSmtpTestEmail(e.target.value)}
              className="h-9 bg-background"
              data-testid="smtp-test-input"
            />
            <Button
              onClick={handleSmtpTest}
              disabled={smtpTesting}
              className="h-9 gold-gradient"
              data-testid="smtp-test-btn"
            >
              <Send className="mr-1.5 h-3.5 w-3.5" />
              {smtpTesting ? 'Sending…' : 'Send test'}
            </Button>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Save your settings first, then send a test message. In mock mode the test is logged under Audit → Email logs.
          </p>
        </div>
        {/* Phase 27.1.5 — per-section save. Sends only smtp_* fields. */}
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            onClick={handleSaveSmtp}
            disabled={saving}
            size="sm"
            className="gold-gradient text-black"
            data-testid="save-smtp-btn"
          >
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
            {saving ? 'Salvataggio…' : 'Save SMTP'}
          </Button>
        </div>
        <SaveSummary section="SMTP" />
      </Sec>

      {/* Phase 2D — Resend (transactional email primary provider) */}
      <Sec title="Resend (Email · primary)" icon={Mail}>
        <div className="rounded-md border border-border bg-background/40 p-3 text-[11px] text-muted-foreground">
          Resend è il provider <strong>primario</strong> per email transazionali (password reset, OTP, ricevute).
          Se non configurato, il sistema fa fallback su SMTP. Le variabili d&apos;ambiente
          <code className="mx-1 rounded bg-secondary px-1 font-mono">RESEND_API_KEY</code>,
          <code className="mx-1 rounded bg-secondary px-1 font-mono">RESEND_FROM_EMAIL</code> e
          <code className="mx-1 rounded bg-secondary px-1 font-mono">RESEND_FROM_NAME</code> hanno priorità sui valori qui sotto.
        </div>
        {data?.resend_env_locks?.resend_api_key && (
          <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-2 text-[11px] text-emerald-800">
            🔒 <strong>API key da environment</strong> — modificabile solo tramite Emergent Secrets. Il form è read-only.
          </div>
        )}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field
            label="API key (re_…)"
            type="password"
            placeholder={data?.resend_api_key_masked || 're_...'}
            value={form.resend_api_key}
            onChange={(e) => setForm({ ...form, resend_api_key: e.target.value })}
            hint="Lascia vuoto per mantenere il valore esistente. Env-locked = non modificabile qui."
            data-testid="resend-api-key-input"
            envLocked={data?.resend_env_locks?.resend_api_key}
          />
          <Field
            label="From email"
            value={form.resend_from_email}
            onChange={(e) => setForm({ ...form, resend_from_email: e.target.value })}
            placeholder="noreply@nymia.me"
            hint="Deve corrispondere a un dominio verificato su Resend (SPF/DKIM/DMARC)."
            data-testid="resend-from-email-input"
            envLocked={data?.resend_env_locks?.resend_from_email}
          />
          <Field
            label="From name"
            value={form.resend_from_name}
            onChange={(e) => setForm({ ...form, resend_from_name: e.target.value })}
            placeholder="Nymia"
            data-testid="resend-from-name-input"
            envLocked={data?.resend_env_locks?.resend_from_name}
          />
          <Field
            label="Public app URL (base per link reset password)"
            value={data?.public_app_url || ''}
            hint="Env-only (PUBLIC_APP_URL). Il backend costruisce il link di reset come `${PUBLIC_APP_URL}/reset-password?token=…`."
            envLocked
            disabled
          />
        </div>
        <div className="rounded-md border border-border bg-background/40 p-3">
          <p className="mb-2 text-xs font-semibold text-foreground">Test Resend</p>
          <div className="flex gap-2">
            <Input
              placeholder="destinatario@example.com"
              value={resendTestEmail}
              onChange={(e) => setResendTestEmail(e.target.value)}
              className="h-9 bg-background"
              data-testid="resend-test-input"
            />
            <Button
              onClick={handleResendTest}
              disabled={resendTesting}
              className="h-9 gold-gradient"
              data-testid="resend-test-btn"
            >
              {resendTesting ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}
              {resendTesting ? 'Invio…' : 'Invia test'}
            </Button>
          </div>
          {resendTestResult && (
            <div
              className={`mt-2 rounded border px-2 py-1.5 text-[11px] ${
                resendTestResult.provider === 'resend' ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-900'
                : resendTestResult.provider === 'smtp' ? 'border-amber-500/40 bg-amber-500/10 text-amber-900'
                : 'border-red-500/40 bg-red-500/10 text-red-900'
              }`}
              data-testid="resend-test-result"
            >
              <p className="font-mono">
                Provider utilizzato: <strong>{resendTestResult.provider || '—'}</strong>
                {resendTestResult.id && <> · id: <code>{resendTestResult.id}</code></>}
              </p>
              {resendTestResult.diagnostics && (
                <ul className="mt-1 list-disc pl-4 text-[10px]">
                  <li>Resend configurato: <strong>{String(resendTestResult.diagnostics.resend_configured)}</strong> (fonte: <code>{resendTestResult.diagnostics.resend_api_key_source}</code>)</li>
                  <li>SMTP configurato: <strong>{String(resendTestResult.diagnostics.smtp_configured)}</strong></li>
                  {resendTestResult.diagnostics.resend_from && <li>From: <code>{resendTestResult.diagnostics.resend_from}</code></li>}
                </ul>
              )}
              {resendTestResult.error && <p className="mt-1 font-mono text-red-800">❌ {resendTestResult.error}</p>}
            </div>
          )}
          <p className="mt-2 text-[11px] text-muted-foreground">
            L&apos;endpoint chiama <code>send_email</code> unificato: Resend primario → SMTP fallback → mock. La risposta mostra quale provider ha risposto.
          </p>
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            onClick={handleSaveResend}
            disabled={saving}
            size="sm"
            className="gold-gradient text-black"
            data-testid="save-resend-btn"
          >
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
            {saving ? 'Salvataggio…' : 'Save Resend'}
          </Button>
        </div>
        <SaveSummary section="Resend" />
      </Sec>

      {/* Phase 95 — SMS / OTP gateway (Creator phone verification) */}
      <Sec title="SMS / OTP (verifica telefono Voce Pro)" icon={Smartphone}>
        <div className="flex flex-wrap items-center gap-2" data-testid="sms-status-row">
          <span className="rounded-md border border-border bg-background/60 px-2 py-1 text-[11px] text-foreground" data-testid="sms-active-provider">
            Provider attivo: <strong className="uppercase">{data?.sms_provider || 'mock'}</strong>
          </span>
          {(data?.sms_provider || 'mock') === 'mock' ? (
            <span className="rounded-md border border-amber-500/40 bg-amber-500/15 px-2 py-1 text-[11px] font-semibold text-amber-700" data-testid="sms-mode-badge">
              MODALITÀ TEST (mock)
            </span>
          ) : (
            <span className="rounded-md border border-emerald-500/40 bg-emerald-500/15 px-2 py-1 text-[11px] font-semibold text-emerald-700" data-testid="sms-mode-badge">
              PRODUZIONE
            </span>
          )}
          <span
            className={`rounded-md border px-2 py-1 text-[11px] font-semibold ${data?.sms_configured ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700' : 'border-red-500/40 bg-red-500/10 text-red-700'}`}
            data-testid="sms-config-status"
          >
            {data?.sms_configured ? '✅ Configurato' : '⚠️ Credenziali mancanti'}
          </span>
        </div>
        <p className="text-[10px] text-muted-foreground" data-testid="sms-credential-sources">
          Origine configurazione — provider: <code>{data?.sms_credential_sources?.sms_provider || '—'}</code>
          {' · '}key: <code>{data?.sms_credential_sources?.sms_api_key || '—'}</code>
          {' · '}secret: <code>{data?.sms_credential_sources?.sms_api_secret || '—'}</code>
          {' · '}sender: <code>{data?.sms_credential_sources?.sms_from || '—'}</code>
        </p>
        <div className="rounded-md border border-border bg-background/40 p-3 text-[11px] text-muted-foreground">
          Gateway usato per l&apos;OTP di verifica telefono delle Voci Pro. Le variabili d&apos;ambiente
          <code className="mx-1 rounded bg-secondary px-1 font-mono">SMS_API_KEY</code> e
          <code className="mx-1 rounded bg-secondary px-1 font-mono">SMS_API_SECRET</code> hanno
          priorità sui valori salvati qui (Emergent Secrets = sede canonica in produzione).
          Con provider <strong>Mock</strong> il codice OTP viene mostrato automaticamente nell&apos;app e nessun SMS reale viene inviato.
        </div>
        {(data?.sms_env_locks?.sms_api_key || data?.sms_env_locks?.sms_api_secret) && (
          <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-2 text-[11px] text-emerald-800" data-testid="sms-env-lock-banner">
            🔒 <strong>Credenziali da environment</strong> — modificabili solo tramite Emergent Secrets. I campi bloccati sono read-only.
          </div>
        )}
        <div>
          <label className="mb-1 block text-xs font-semibold text-foreground">Provider SMS</label>
          <select
            value={form.sms_provider}
            onChange={(e) => setForm({ ...form, sms_provider: e.target.value })}
            className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm md:max-w-xs"
            data-testid="sms-provider-select"
          >
            <option value="mock">Mock / Test (nessun SMS reale)</option>
            <option value="plivo">Plivo</option>
            <option value="vonage">Vonage</option>
          </select>
        </div>
        {form.sms_provider !== 'mock' && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field
              label={form.sms_provider === 'plivo' ? 'Auth ID' : 'API Key'}
              type="password"
              placeholder={data?.sms_api_key_masked || (form.sms_provider === 'plivo' ? 'MAXXXXXXXXXXXXXXXXX' : 'abcd1234')}
              value={form.sms_api_key}
              onChange={(e) => setForm({ ...form, sms_api_key: e.target.value })}
              hint="Lascia vuoto per mantenere il valore salvato."
              envLocked={data?.sms_env_locks?.sms_api_key}
              data-testid="sms-api-key-input"
            />
            <Field
              label={form.sms_provider === 'plivo' ? 'Auth Token' : 'API Secret'}
              type="password"
              placeholder={data?.sms_api_secret_masked || '••••••••'}
              value={form.sms_api_secret}
              onChange={(e) => setForm({ ...form, sms_api_secret: e.target.value })}
              hint="Lascia vuoto per mantenere il valore salvato."
              envLocked={data?.sms_env_locks?.sms_api_secret}
              data-testid="sms-api-secret-input"
            />
            <Field
              label="Sender ID"
              placeholder="Nymia"
              value={form.sms_from}
              onChange={(e) => setForm({ ...form, sms_from: e.target.value })}
              hint="Mittente alfanumerico mostrato sul telefono (es. Nymia)."
              data-testid="sms-sender-input"
            />
          </div>
        )}
        <div className="rounded-md border border-border bg-background/40 p-3">
          <p className="mb-2 text-xs font-semibold text-foreground">Invia SMS di test</p>
          <div className="flex gap-2">
            <Input
              placeholder="+393331234567"
              value={smsTestPhone}
              onChange={(e) => setSmsTestPhone(e.target.value)}
              className="h-9 bg-background md:max-w-xs"
              data-testid="sms-test-phone-input"
            />
            <Button
              onClick={handleSmsTest}
              disabled={smsTesting}
              className="h-9 gold-gradient"
              data-testid="sms-test-btn"
            >
              {smsTesting ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}
              {smsTesting ? 'Invio…' : 'Invia test'}
            </Button>
          </div>
          {smsTestResult && (
            <div
              className={`mt-2 rounded border px-2 py-1.5 text-[11px] ${
                smsTestResult.simulated ? 'border-amber-500/40 bg-amber-500/10 text-amber-900'
                : smsTestResult.sent ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-900'
                : 'border-red-500/40 bg-red-500/10 text-red-900'
              }`}
              data-testid="sms-test-result"
            >
              {smsTestResult.simulated && (
                <p className="font-mono">🧪 <strong>TEST SIMULATO</strong> — provider mock, nessun SMS reale inviato.</p>
              )}
              {smsTestResult.sent && (
                <p className="font-mono">
                  ✅ Inviato via <strong>{smsTestResult.provider}</strong>
                  {smsTestResult.message_id && <> · id: <code>{smsTestResult.message_id}</code></>}
                </p>
              )}
              {smsTestResult.error && <p className="mt-1 font-mono">❌ {smsTestResult.error}</p>}
              {smsTestResult.diagnostics && (
                <ul className="mt-1 list-disc pl-4 text-[10px]">
                  <li>Provider: <strong>{smsTestResult.diagnostics.provider}</strong> · Sender: <code>{smsTestResult.diagnostics.sender_id}</code></li>
                  {smsTestResult.diagnostics.credentials_configured !== null && smsTestResult.diagnostics.credentials_configured !== undefined && (
                    <li>Credenziali configurate: <strong>{String(smsTestResult.diagnostics.credentials_configured)}</strong> (key da: <code>{smsTestResult.diagnostics.api_key_source}</code>)</li>
                  )}
                </ul>
              )}
            </div>
          )}
          <p className="mt-2 text-[11px] text-muted-foreground">
            Il test usa il provider attualmente <strong>salvato</strong> (salva prima le modifiche). Nessuna credenziale viene mai esposta al browser.
          </p>
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            onClick={handleSaveSms}
            disabled={saving}
            size="sm"
            className="gold-gradient text-black"
            data-testid="save-sms-btn"
          >
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
            {saving ? 'Salvataggio…' : 'Salva configurazione SMS'}
          </Button>
        </div>
        <SaveSummary section="SMS" />
      </Sec>

      <Sec title="Notifiche Live via Email (Broadcast)" icon={Radio}>
        <div className="flex items-center justify-between rounded-md border border-border bg-background/40 px-3 py-2">
          <div className="pr-4">
            <p className="text-sm font-semibold">Enable Global Live Notifications</p>
            <p className="text-[11px] text-muted-foreground">
              Quando è ON, ogni volta che una Live parte (Now o programmata), viene creata una Live
              programmata o una Voce Pro diventa disponibile per Chiamate, viene inviata una email a
              TUTTI gli utenti registrati (esclusi la Voce Pro e chi ha annullato l&apos;iscrizione).
              Con OFF nessuna email di broadcast viene inviata.
            </p>
          </div>
          <Switch
            checked={form.live_broadcast_emails_enabled}
            onCheckedChange={(v) => setForm({ ...form, live_broadcast_emails_enabled: v })}
            data-testid="live-broadcast-toggle"
          />
        </div>
        <p className="text-[11px] text-muted-foreground">
          Ogni email include un link di annullamento iscrizione obbligatorio. Le email partono in
          background (~2/sec) tramite Resend; le live private non vengono mai annunciate e la
          disponibilità Chiamate ha un cooldown di 6 ore per Voce Pro.
        </p>
        <div className="rounded-md border border-border bg-background/40 p-3">
          <p className="mb-1 text-xs font-semibold text-foreground">Test template</p>
          <p className="mb-2 text-[11px] text-muted-foreground">
            Invia le 3 email di esempio (Live Ora, Programmata, Chiamate) con dati fittizi SOLO al
            tuo indirizzo admin. Funziona anche con l&apos;interruttore su OFF — nessun utente le riceve.
          </p>
          <Button
            onClick={handleBroadcastPreview}
            disabled={broadcastPreviewSending}
            size="sm"
            variant="outline"
            className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
            data-testid="live-broadcast-preview-btn"
          >
            {broadcastPreviewSending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Mail className="mr-1 h-4 w-4" />}
            {broadcastPreviewSending ? 'Invio in corso…' : 'Send Preview Email to Me'}
          </Button>
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            onClick={() => saveSection('LiveBroadcast', { live_broadcast_emails_enabled: form.live_broadcast_emails_enabled })}
            disabled={saving}
            size="sm"
            className="gold-gradient text-black"
            data-testid="live-broadcast-save-btn"
          >
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
            {saving ? 'Salvataggio…' : 'Salva Notifiche Live'}
          </Button>
        </div>
        <SaveSummary section="LiveBroadcast" />
      </Sec>

      <Sec title="Notifiche Piattaforma (campanella + push web + push app)" icon={Radio}>
        <p className="text-[11px] text-muted-foreground">
          Live iniziata, Live programmata e &quot;Voce Pro disponibile One-to-One&quot; arrivano in campanella
          e come push a TUTTI gli utenti attivi (esclusi chi genera l&apos;evento, i bloccati e gli account
          di test). Con OFF tornano ad arrivare SOLO ai Preferiti. Le live private restano sempre solo Preferiti;
          la disponibilità One-to-One ha un cooldown di 6 ore per Voce Pro. Le notifiche a tutta la
          piattaforma si auto-cancellano dopo 30 giorni.
        </p>
        <div className="flex items-center justify-between rounded-md border border-border bg-background/40 px-3 py-2">
          <div className="pr-4">
            <p className="text-sm font-semibold">Eventi dei Voce Pro → tutta la piattaforma</p>
            <p className="text-[11px] text-muted-foreground">OFF = solo Preferiti del Voce Pro.</p>
          </div>
          <Switch
            checked={form.notify_all_voce_pro}
            onCheckedChange={(v) => setForm({ ...form, notify_all_voce_pro: v })}
            data-testid="notify-all-voce-pro-toggle"
          />
        </div>
        <div className="flex items-center justify-between rounded-md border border-border bg-background/40 px-3 py-2">
          <div className="pr-4">
            <p className="text-sm font-semibold">Eventi degli utenti → tutta la piattaforma</p>
            <p className="text-[11px] text-muted-foreground">Live gratis degli utenti normali. OFF = solo Preferiti.</p>
          </div>
          <Switch
            checked={form.notify_all_users}
            onCheckedChange={(v) => setForm({ ...form, notify_all_users: v })}
            data-testid="notify-all-users-toggle"
          />
        </div>
        <div className="flex items-center justify-between rounded-md border border-border bg-background/40 px-3 py-2">
          <div className="pr-4">
            <p className="text-sm font-semibold">Email &quot;Un Voce Pro ti ha scritto&quot;</p>
            <p className="text-[11px] text-muted-foreground">Quando un Voce Pro scrive a un utente e il messaggio resta non letto per 10 minuti, l&apos;utente riceve un&apos;email (senza il testo del messaggio). Max 1 email al giorno per coppia Voce Pro → utente; rispetta la preferenza email dell&apos;utente.</p>
          </div>
          <Switch
            checked={form.vp_message_emails_enabled}
            onCheckedChange={(v) => setForm({ ...form, vp_message_emails_enabled: v })}
            data-testid="vp-message-emails-toggle"
          />
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            onClick={() => saveSection('PlatformNotify', { notify_all_voce_pro: form.notify_all_voce_pro, notify_all_users: form.notify_all_users, vp_message_emails_enabled: form.vp_message_emails_enabled })}
            disabled={saving}
            size="sm"
            className="gold-gradient text-black"
            data-testid="platform-notify-save-btn"
          >
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
            {saving ? 'Salvataggio…' : 'Salva Notifiche Piattaforma'}
          </Button>
        </div>
        <SaveSummary section="PlatformNotify" />
      </Sec>

      <Sec title="Storage (Wasabi S3-compatible)" icon={Database}>
        {/* Phase 27.1 — hard warning: detect the "silent local fallback"
            footgun. If the admin disabled mock mode but didn't enter the
            Wasabi credentials, the backend silently writes uploads to
            the pod's ephemeral disk → files vanish on the next redeploy.
            This banner makes the misconfig impossible to miss. */}
        {!form.storage_mock_mode && !(data?.wasabi_access_key_masked) && (
          <div
            className="rounded-md border-2 border-red-500/60 bg-red-500/10 p-3 text-sm"
            data-testid="storage-misconfig-warning"
          >
            <p className="font-semibold text-red-700">⚠️ Storage misconfigurato — file persi al prossimo redeploy</p>
            <p className="mt-1 text-xs text-red-800/90">
              Hai disattivato il <strong>Mock mode</strong> ma <strong>non hai inserito le chiavi Wasabi</strong>.
              Il backend sta facendo silent fallback al disco locale del pod (effimero). Tutti i file caricati
              spariranno al prossimo restart/redeploy. <strong>Inserisci Access key + Secret key</strong>
              qui sotto e salva, oppure riattiva temporaneamente il Mock mode finché non hai le credenziali.
            </p>
          </div>
        )}
        <div className="flex items-center justify-between rounded-md border border-border bg-background/40 px-3 py-2">
          <div>
            <p className="text-sm font-semibold">Mock mode</p>
            <p className="text-[11px] text-muted-foreground">When off, post media is uploaded to Wasabi instead of the local disk.</p>
          </div>
          <Switch
            checked={form.storage_mock_mode}
            onCheckedChange={(v) => setForm({ ...form, storage_mock_mode: v })}
            data-testid="storage-mock-toggle"
            disabled={!!data?.wasabi_env_locks?.storage_mock_mode}
          />
        </div>
        <Field
          label="Access key"
          placeholder={data?.wasabi_access_key_masked || ''}
          value={form.wasabi_access_key}
          onChange={(e) => setForm({ ...form, wasabi_access_key: e.target.value })}
          hint="Leave blank to keep existing value"
          envLocked={!!data?.wasabi_env_locks?.wasabi_access_key}
          name="wasabi-access-key"
        />
        <Field
          label="Secret key"
          type="password"
          placeholder={data?.wasabi_secret_key_masked || ''}
          value={form.wasabi_secret_key}
          onChange={(e) => setForm({ ...form, wasabi_secret_key: e.target.value })}
          hint="Leave blank to keep existing value"
          envLocked={!!data?.wasabi_env_locks?.wasabi_secret_key}
          name="wasabi-secret-key"
        />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field
            label="Bucket name"
            value={form.wasabi_bucket_name}
            onChange={(e) => setForm({ ...form, wasabi_bucket_name: e.target.value })}
            placeholder="nymia-media"
            envLocked={!!data?.wasabi_env_locks?.wasabi_bucket_name}
            name="wasabi-bucket-name"
          />
          <Field
            label="Region"
            value={form.wasabi_region}
            onChange={(e) => setForm({ ...form, wasabi_region: e.target.value })}
            placeholder="us-east-1"
            envLocked={!!data?.wasabi_env_locks?.wasabi_region}
            name="wasabi-region"
          />
        </div>
        <Field
          label="Endpoint (optional)"
          value={form.wasabi_endpoint}
          onChange={(e) => setForm({ ...form, wasabi_endpoint: e.target.value })}
          placeholder="https://s3.us-east-1.wasabisys.com"
          hint="Leave blank to use the default Wasabi endpoint for your region"
          envLocked={!!data?.wasabi_env_locks?.wasabi_endpoint}
          name="wasabi-endpoint"
        />
        <Field
          label="Public base URL (optional)"
          value={form.wasabi_public_base_url}
          onChange={(e) => setForm({ ...form, wasabi_public_base_url: e.target.value })}
          placeholder="https://cdn.nymia.app"
          hint="If your bucket is public via a CDN, paste the base URL here. Otherwise pre-signed URLs are used."
          envLocked={!!data?.wasabi_env_locks?.wasabi_public_base_url}
          name="wasabi-public-base-url"
        />
        {/* Phase 27.1.4 — per-section save. Sends only wasabi_* + storage_mock_mode
            so a 409 on LiveKit (env-locked) doesn't block this save. */}
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            onClick={handleSaveWasabi}
            disabled={saving}
            size="sm"
            className="gold-gradient text-black"
            data-testid="save-wasabi-btn"
          >
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
            {saving ? 'Salvataggio…' : 'Save Wasabi'}
          </Button>
        </div>
        <SaveSummary section="Wasabi" />
        <WasabiConnectionTester form={form} />
      </Sec>

      <Sec title="Meta Pixel & Conversions API" icon={BarChart3}>
        <div className="flex items-center justify-between rounded-md border border-border bg-background/40 px-3 py-2">
          <div>
            <p className="text-sm font-semibold">Mock mode</p>
            <p className="text-[11px] text-muted-foreground">When off, events are sent to Meta&apos;s Graph API server-side AND the browser Pixel fires.</p>
          </div>
          <Switch
            checked={form.meta_mock_mode}
            onCheckedChange={(v) => setForm({ ...form, meta_mock_mode: v })}
            data-testid="meta-mock-toggle"
          />
        </div>
        <Field
          label="Pixel ID"
          value={form.meta_pixel_id}
          onChange={(e) => setForm({ ...form, meta_pixel_id: e.target.value })}
          placeholder="123456789012345"
        />
        <Field
          label="Access token (Conversions API)"
          type="password"
          placeholder={data?.meta_access_token_masked || ''}
          value={form.meta_access_token}
          onChange={(e) => setForm({ ...form, meta_access_token: e.target.value })}
          hint="Generate from Business Manager → Events Manager → Settings. Leave blank to keep existing value."
        />
        <Field
          label="Test event code (optional)"
          value={form.meta_test_event_code}
          onChange={(e) => setForm({ ...form, meta_test_event_code: e.target.value })}
          placeholder="TEST12345"
          hint="Use during integration testing — events will appear in the Events Manager Test Events panel."
        />
        <p className="text-[11px] text-muted-foreground">
          Tracked events: <code className="rounded bg-muted px-1">CompleteRegistration</code>,{' '}
          <code className="rounded bg-muted px-1">Purchase</code> (paid post unlock, live ticket).
        </p>
      </Sec>

      {/* Phase 3 · Meta CAPI Launch Certification Card (audit + diagnostic) */}
      <MetaDiagnosticCard />

      <Sec title="AI Content Moderation (Sightengine)" icon={ShieldCheck}>
        <div className="flex items-center justify-between rounded-md border border-border bg-background/40 px-3 py-2">
          <div>
            <p className="text-sm font-semibold">Enable AI Content Moderation</p>
            <p className="text-[11px] text-muted-foreground">
              Scans uploaded images for nudity/minors/violence/gore/hate symbols before publication.
              Severe content is auto-rejected; borderline content is routed to manual review.
            </p>
          </div>
          <Switch
            checked={form.sightengine_enabled}
            onCheckedChange={(v) => setForm({ ...form, sightengine_enabled: v })}
            data-testid="sightengine-toggle"
          />
        </div>
        <Field
          label="API user"
          value={form.sightengine_api_user}
          onChange={(e) => setForm({ ...form, sightengine_api_user: e.target.value })}
          placeholder="123456789"
        />
        <Field
          label="API secret"
          type="password"
          placeholder={data?.sightengine_api_secret_masked || ''}
          value={form.sightengine_api_secret}
          onChange={(e) => setForm({ ...form, sightengine_api_secret: e.target.value })}
          hint="Get your keys from https://sightengine.com/console. Leave blank to keep the existing value."
        />
        <p className="text-[11px] text-muted-foreground">
          Models used: <code className="rounded bg-muted px-1">nudity-2.1, wad, offensive, gore, violence, self-harm</code>.
        </p>
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            onClick={handleSaveSightengine}
            disabled={saving}
            size="sm"
            className="gold-gradient text-black"
            data-testid="save-sightengine-btn"
          >
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
            {saving ? 'Salvataggio…' : 'Salva impostazioni Sightengine'}
          </Button>
        </div>
        <SaveSummary section="Sightengine" />
      </Sec>

      <Sec title="Approval policy" icon={ShieldCheck}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold">Require paid post approval</p>
            <p className="text-[11px] text-muted-foreground">New paid posts will be hidden until an admin approves them.</p>
          </div>
          <Switch checked={form.require_paid_post_approval} onCheckedChange={(v) => setForm({ ...form, require_paid_post_approval: v })} />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold">Require Voice Pro approval</p>
            <p className="text-[11px] text-muted-foreground">New Voice Pros start as pending (manual onboarding).</p>
          </div>
          <Switch checked={form.require_creator_approval} onCheckedChange={(v) => setForm({ ...form, require_creator_approval: v })} />
        </div>
      </Sec>

      <Sec title="Legal footer (Phase 18)" icon={FileCheck2}>
        <p className="text-[11px] text-muted-foreground">
          Displayed at the bottom of all public Auth pages (login & register).
          Use this for VAT / company details required by GDPR & EU consumer law.
        </p>
        <Field
          label="Footer text"
          value={form.legal_footer}
          onChange={(e) => setForm({ ...form, legal_footer: e.target.value })}
          placeholder="All rights reserved 2026 to ..."
          hint="Visible globally — keep it short and accurate."
          data-testid="settings-legal-footer-input"
        />
        {/* Phase 27.1.5 — "General" save covers Meta + Sightengine + Approval + Legal
            in a single payload (none of these are env-locked). */}
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            onClick={handleSaveGeneral}
            disabled={saving}
            size="sm"
            className="gold-gradient text-black"
            data-testid="save-general-btn"
          >
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
            {saving ? 'Salvataggio…' : 'Save General'}
          </Button>
        </div>
        <SaveSummary section="General" />
      </Sec>

      <Sec title="Maintenance" icon={Trash2}>
        <UserLoginDiagnosticPanel />
        <div className="my-4 h-px bg-border" />
        <CleanTestDataPanel />
        <div className="my-4 h-px bg-border" />
        <LegacyPurgePanel />
        <div className="my-4 h-px bg-border" />
        <LiveDiagnosticPanel />
      </Sec>
    </div>
  );
};

// ============= PHASE 17 — Tip Presets editor · REMOVED here Jul 2026 =============
// The component was extracted to `components/admin/TipPresetsEditor.jsx`
// and mounted inside `AdminWalletPackages` so admin has a single Wallet
// operational page (packages · stats · tip jar). Keeping this comment as
// a landmark so any future search for the old inline definition lands
// here. The API contract is unchanged (`app_settings.tip_presets`).

// ============= PHASE 17 — Email Manager =============
const EmailManagerTab = () => {
  const { t } = useTranslation('admin');
  const [emails, setEmails] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [selected, setSelected] = React.useState(null);
  const [note, setNote] = React.useState('');
  const [link, setLink] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const rows = await adminListEmailLogs(200);
      setEmails(rows);
    } catch (e) {
      toast({ title: 'Could not load emails', description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  React.useEffect(() => { load(); }, []);

  const open = (e) => {
    setSelected(e);
    setNote(e.admin_note || '');
    setLink(e.extra_link || '');
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await adminEnrichEmailLog(selected.id, { admin_note: note, extra_link: link });
      toast({ title: 'Email enriched' });
      // Refresh the in-place row
      setEmails((arr) => arr.map((x) => (x.id === selected.id ? { ...x, admin_note: note, extra_link: link } : x)));
      setSelected({ ...selected, admin_note: note, extra_link: link });
    } catch (e) {
      toast({ title: 'Could not save', description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid="email-manager-tab">
      <div className="lg:col-span-2 rounded-2xl border border-border bg-card">
        <header className="flex items-center justify-between border-b border-border px-4 py-3">
          <h3 className="flex items-center gap-2 text-sm font-semibold"><Mail className="h-4 w-4" /> {t('page.sections.email_log')}</h3>
          <button onClick={load} className="text-xs text-muted-foreground hover:gold-text" data-testid="email-manager-refresh">
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </header>
        <div className="max-h-[560px] overflow-y-auto">
          {emails.length === 0 ? (
            <p className="px-4 py-10 text-center text-xs text-muted-foreground">No emails recorded yet.</p>
          ) : emails.map((e) => (
            <button
              key={e.id || e.created_at}
              onClick={() => open(e)}
              className={`flex w-full flex-col items-start gap-1 border-b border-border/60 px-4 py-2.5 text-left hover:bg-secondary ${
                selected?.id === e.id ? 'bg-secondary' : ''
              }`}
              data-testid={`email-log-row-${e.id || 'na'}`}
            >
              <div className="flex w-full items-center justify-between gap-2">
                <p className="truncate text-sm font-semibold">{e.subject}</p>
                <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${e.mock ? 'bg-muted text-muted-foreground' : 'bg-emerald-500/20 text-emerald-300'}`}>
                  {e.mock ? 'MOCK' : 'SENT'}
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground">to {e.to} · {new Date(e.created_at).toLocaleString()}</p>
              {e.admin_note && (
                <p className="line-clamp-1 text-[11px] gold-text">📝 {e.admin_note}</p>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-4" data-testid="email-manager-detail">
        {selected ? (
          <>
            <h4 className="text-sm font-semibold">{selected.subject}</h4>
            <p className="mt-0.5 text-[11px] text-muted-foreground">{selected.category} · {selected.to}</p>
            <pre className="mt-3 max-h-48 overflow-auto whitespace-pre-wrap rounded-md bg-secondary p-3 text-[12px] text-foreground" data-testid="email-body">
              {selected.text_body || '(no body)'}
            </pre>
            <div className="mt-3 space-y-2">
              <div>
                <label className="mb-1 block text-[11px] font-semibold text-muted-foreground">Admin note (visible only to staff)</label>
                <Input
                  value={note}
                  onChange={(ev) => setNote(ev.target.value)}
                  placeholder="Followed up by phone…"
                  data-testid="email-admin-note"
                />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold text-muted-foreground">Extra link (e.g. help article)</label>
                <Input
                  value={link}
                  onChange={(ev) => setLink(ev.target.value)}
                  placeholder="https://…"
                  data-testid="email-extra-link"
                />
              </div>
              <Button onClick={save} disabled={saving} className="w-full gold-gradient text-black" data-testid="email-enrich-save">
                {saving ? 'Saving…' : 'Save enrichment'}
              </Button>
            </div>
          </>
        ) : (
          <p className="py-12 text-center text-xs text-muted-foreground">Select an email to inspect.</p>
        )}
      </div>
    </div>
  );
};

const RefundsTab = () => {
  const { t } = useTranslation('admin');
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [purchaseId, setPurchaseId] = useState('');
  const [reverseReason, setReverseReason] = useState('');
  const [reversing, setReversing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const rows = await adminListRefunds(filter || undefined);
      setItems(rows);
    } catch (e) {
      toast({ title: 'Could not load refunds', description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const approve = async (id) => {
    const note = window.prompt('Optional admin note for the buyer:') || '';
    setBusyId(id);
    try {
      await adminApproveRefund(id, note);
      toast({ title: 'Refund approved' });
      await load();
    } catch (e) {
      toast({ title: 'Could not approve', description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally { setBusyId(null); }
  };

  const decline = async (id) => {
    const note = window.prompt('Reason for decline (sent to user):') || '';
    setBusyId(id);
    try {
      await adminDeclineRefund(id, note);
      toast({ title: 'Refund declined' });
      await load();
    } catch (e) {
      toast({ title: 'Could not decline', description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally { setBusyId(null); }
  };

  const reverseTip = async () => {
    if (!purchaseId.trim() || !reverseReason.trim()) {
      toast({ title: 'Purchase ID and reason are required', variant: 'destructive' });
      return;
    }
    setReversing(true);
    try {
      const r = await adminReversePurchase(purchaseId.trim(), reverseReason.trim());
      toast({ title: 'Tip annullato', description: `Buyer +${Math.round(r.buyer_refunded || 0)} cr · Seller −${Math.round(r.seller_deducted || 0)} cr` });
      setPurchaseId('');
      setReverseReason('');
    } catch (e) {
      toast({ title: 'Could not reverse', description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally { setReversing(false); }
  };

  const statusBadge = (s) => {
    const cls = s === 'pending' ? 'bg-amber-500/20 gold-text'
      : s === 'approved' ? 'bg-emerald-500/20 text-emerald-300'
      : 'bg-red-500/20 text-red-300';
    return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${cls}`}>{s}</span>;
  };

  return (
    <div className="space-y-6" data-testid="admin-refunds-panel">
      <div className="rounded-xl border border-border bg-card">
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="flex items-center gap-2 text-sm font-semibold"><RefreshCcw className="h-4 w-4 gold-text" /> {t('page.sections.refunds')}</h2>
          <div className="flex items-center gap-2 text-xs">
            {['pending', 'approved', 'declined', ''].map((s) => (
              <button
                key={s || 'all'}
                onClick={() => setFilter(s)}
                data-testid={`refunds-filter-${s || 'all'}`}
                className={`rounded-md px-2 py-1 font-semibold ${
                  filter === s ? 'gold-gradient' : 'text-muted-foreground hover:bg-secondary'
                }`}
              >
                {s ? s : 'All'}
              </button>
            ))}
          </div>
        </header>

        {loading ? (
          <div className="flex h-32 items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-border border-t-amber-400" />
          </div>
        ) : items.length === 0 ? (
          <p className="px-5 py-12 text-center text-sm text-muted-foreground" data-testid="refunds-empty">
            No refund requests {filter ? `(${filter})` : ''}.
          </p>
        ) : (
          <ul className="divide-y divide-border" data-testid="refunds-list">
            {items.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3" data-testid={`refund-row-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground">@{r.username || r.user_id}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {new Date(r.created_at).toLocaleString()} · {r.reason ? `"${r.reason}"` : 'No reason given'}
                  </p>
                  {r.admin_note ? <p className="mt-1 text-[11px] gold-text">Admin: {r.admin_note}</p> : null}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold tabular-nums">{fmtCredits(r.amount)}</span>
                  {statusBadge(r.status)}
                  {r.status === 'pending' && (
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        disabled={busyId === r.id}
                        onClick={() => approve(r.id)}
                        className="h-7 bg-emerald-500 px-2 text-xs hover:bg-emerald-600"
                        data-testid={`refund-approve-${r.id}`}
                      >Approve</Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busyId === r.id}
                        onClick={() => decline(r.id)}
                        className="h-7 px-2 text-xs text-red-400 hover:bg-red-500/10"
                        data-testid={`refund-decline-${r.id}`}
                      >Decline</Button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold"><Undo2 className="h-4 w-4 gold-text" /> {t('page.sections.reverse_tip')}</h3>
        <p className="mb-3 text-xs text-muted-foreground">
          Refunds the buyer in full and deducts the same <span className="gold-text">net amount</span> from the Voice Pro
          (preferring their 30-day pending balance). Use only for confirmed chargebacks or fraud.
        </p>
        <div className="grid gap-2 md:grid-cols-[1fr_2fr_auto]">
          <Input
            value={purchaseId}
            onChange={(e) => setPurchaseId(e.target.value)}
            placeholder="Purchase ID (tip)"
            className="h-9 text-xs"
            data-testid="reverse-purchase-id-input"
          />
          <Input
            value={reverseReason}
            onChange={(e) => setReverseReason(e.target.value)}
            placeholder="Reason (will be logged)"
            className="h-9 text-xs"
            data-testid="reverse-reason-input"
          />
          <Button
            onClick={reverseTip}
            disabled={reversing}
            className="h-9 gold-gradient text-xs"
            data-testid="reverse-submit-btn"
          >
            {reversing ? 'Reversing…' : 'Reverse'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const AdminPage = () => {
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation('admin');
  // Phase 17 — Super Admin gets the integration keys + tip presets; staff are read-only on Settings.
  const isSuperAdmin = !!user?.is_admin && (!user?.role || user.role === 'admin');
  const [tab, setTab] = useState('overview');
  const [users, setUsers] = useState([]);
  // Phase 85 — Users tab: real-time search + VIP filter (client-side).
  const [userSearch, setUserSearch] = useState('');
  const [userVipFilter, setUserVipFilter] = useState('all');
  const visibleUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase().replace(/^@/, '');
    return users.filter((u) => {
      if (userVipFilter === 'vip' && u.creator_status !== 'active') return false;
      if (userVipFilter === 'normal' && u.creator_status === 'active') return false;
      if (!q) return true;
      return [u.username, u.email, u.full_name].some((v) => (v || '').toLowerCase().includes(q));
    });
  }, [users, userSearch, userVipFilter]);
  // Server-side search fallback: users older than the newest 500 are not in
  // `users` — fetch matches for the query and merge them in (deduped by id).
  useEffect(() => {
    const q = userSearch.trim().replace(/^@/, '');
    if (q.length < 2) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const found = await adminListUsers(q);
        if (cancelled || !Array.isArray(found) || !found.length) return;
        setUsers((us) => {
          const known = new Set(us.map((u) => u.id));
          const extra = found.filter((u) => !known.has(u.id));
          return extra.length ? [...us, ...extra] : us;
        });
      } catch (_e) { /* ignore */ }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [userSearch]); // eslint-disable-line react-hooks/exhaustive-deps
  const [stats, setStats] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [pending, setPending] = useState([]);
  const [approvalPreview, setApprovalPreview] = useState(null);
  const [finance, setFinance] = useState(null);
  const [activeLives, setActiveLives] = useState([]);
  const [loading, setLoading] = useState(true);
  // Phase 42 — Stripe/fiscal billing profile dialog
  const [billingDialogUserId, setBillingDialogUserId] = useState(null);
  // Jul 2026 — manual wallet adjustment dialog (holds the user obj so
  // the dialog can display the current balance without a re-fetch).
  const [walletDialogUser, setWalletDialogUser] = useState(null);
  // Phase 48C · Help Center notification badge — polled every 30s so
  // the "Assistenza" tab shows a live unread count for chats + tickets.
  const [helpUnread, setHelpUnread] = useState(0);
  // Phase 50 · Reports badge (nuova + in_revisione).
  const [reportsNew, setReportsNew] = useState(0);

  // Block non-admins with a clear error toast on direct URL access
  useEffect(() => {
    if (!authLoading && user && !user.is_admin) {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to access the Admin area.',
        variant: 'destructive',
      });
    }
  }, [authLoading, user, toast]);

  const load = async () => {
    setLoading(true);
    try {
      const calls = [
        adminListUsers(), adminStats(), adminListPayouts(),
        adminPendingPosts(), adminFinance(), adminActiveLives(),
      ];
      // Phase 17 — staff cannot read integration keys; skip the Settings call.
      if (isSuperAdmin) calls.push(adminGetSettings()); else calls.push(Promise.resolve(null));
      const [u, s, p, pend, fin, al, sett] = await Promise.all(calls);
      setUsers(u); setStats(s); setPayouts(p);
      setSettings(sett); setPending(pend); setFinance(fin); setActiveLives(al);
    } catch (err) {
      toast({ title: 'Could not load admin data', description: err.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_admin]);

  // Phase 48C · poll unread help-center counters. Interval-based to
  // avoid keeping a websocket open just for a badge; 30s is small
  // enough to feel live without eating bandwidth.
  useEffect(() => {
    if (!user?.is_admin) return undefined;
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const res = await api.get('/admin/help/unread-summary');
        if (!cancelled) setHelpUnread(res.data.total || 0);
      } catch (_e) { /* silent */ }
    };
    fetchUnread();
    const id = window.setInterval(fetchUnread, 30000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [user?.is_admin]);

  // Phase 50 · Reports pending counter (new + in_revisione).
  useEffect(() => {
    if (!user?.is_admin) return undefined;
    let cancelled = false;
    const fetchCounts = async () => {
      try {
        const res = await api.get('/admin/reports/counts');
        if (!cancelled) setReportsNew((res.data?.nuova || 0) + (res.data?.in_revisione || 0));
      } catch (_e) { /* silent */ }
    };
    fetchCounts();
    const id = window.setInterval(fetchCounts, 30000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [user?.is_admin]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/" replace />;

  const saveFee = async (userId, fee) => {
    try {
      const updated = await adminUpdateFee(userId, fee);
      setUsers((us) => us.map((u) => (u.id === userId ? { ...u, ...updated } : u)));
      toast({ title: 'Fee updated' });
    } catch { toast({ title: 'Could not update', variant: 'destructive' }); }
  };

  const setStatus = async (userId, status) => {
    try {
      await adminSetUserStatus(userId, status);
      setUsers((us) => us.map((u) => (u.id === userId ? { ...u, creator_status: status } : u)));
      toast({ title: `User ${status}` });
    } catch { toast({ title: 'Could not change status', variant: 'destructive' }); }
  };

  const pauseUser = async (userId) => {
    try {
      await adminPauseUser(userId);
      setUsers((us) => us.map((u) => (u.id === userId ? { ...u, status: 'paused' } : u)));
      toast({ title: 'User paused' });
    } catch (e) {
      toast({
        title: 'Could not pause',
        description: e.response?.data?.detail || '',
        variant: 'destructive',
      });
    }
  };

  const resumeUser = async (userId) => {
    try {
      await adminResumeUser(userId);
      setUsers((us) => us.map((u) => (u.id === userId ? { ...u, status: 'active' } : u)));
      toast({ title: 'User resumed' });
    } catch (e) {
      toast({
        title: 'Could not resume',
        description: e.response?.data?.detail || '',
        variant: 'destructive',
      });
    }
  };

  // Giu 2026 — STANDBY: hidden from feeds, user can still log in and edit.
  const [cardPreviewUser, setCardPreviewUser] = useState(null);
  const standbyUser = async (userId, username) => {
    const reason = window.prompt(`Mettere @${username} in standby?\nIl profilo sparisce da feed, ricerca e live; l'utente può accedere e modificare.\nMotivo (facoltativo):`, '');
    if (reason === null) return;
    try {
      await adminStandbyUser(userId, reason);
      setUsers((us) => us.map((u) => (u.id === userId ? { ...u, standby: true, standby_reason: reason || null } : u)));
      toast({ title: `@${username} in standby` });
    } catch (e) {
      toast({ title: 'Standby non riuscito', description: e.response?.data?.detail || '', variant: 'destructive' });
    }
  };
  const releaseStandby = async (userId) => {
    try {
      await adminReleaseStandby(userId);
      setUsers((us) => us.map((u) => (u.id === userId ? { ...u, standby: false, standby_reason: null } : u)));
      toast({ title: 'Profilo riattivato nei feed' });
    } catch (e) {
      toast({ title: 'Riattivazione non riuscita', description: e.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const removeUser = async (userId, username) => {
    if (!window.confirm(`Permanently delete @${username} and all their content? This cannot be undone.`)) return;
    try {
      await adminDeleteUser(userId);
      setUsers((us) => us.filter((u) => u.id !== userId));
      toast({ title: 'User deleted' });
    } catch (e) {
      toast({
        title: 'Could not delete',
        description: e.response?.data?.detail || '',
        variant: 'destructive',
      });
    }
  };

  const suspendMonet = async (userId, username) => {
    if (!window.confirm(`Suspend monetization for @${username}? They will keep posting free content but cannot publish paid content, paid lives or receive tips.`)) return;
    try {
      await adminSuspendMonetization(userId);
      setUsers((us) => us.map((u) => (u.id === userId ? { ...u, monetization_enabled: false } : u)));
      toast({ title: 'Monetization suspended' });
    } catch (e) {
      toast({
        title: 'Could not suspend',
        description: e.response?.data?.detail || '',
        variant: 'destructive',
      });
    }
  };

  const resumeMonet = async (userId) => {
    try {
      await adminResumeMonetization(userId);
      setUsers((us) => us.map((u) => (u.id === userId ? { ...u, monetization_enabled: true } : u)));
      toast({ title: 'Monetization resumed' });
    } catch (e) {
      toast({
        title: 'Could not resume',
        description: e.response?.data?.detail || '',
        variant: 'destructive',
      });
    }
  };

  const setUserRole = async (userId, role) => {
    try {
      const out = await adminSetUserRole(userId, role);
      setUsers((us) => us.map((u) => (u.id === userId ? { ...u, role: out.role, is_admin: out.is_admin } : u)));
      toast({ title: `Role set to ${out.role}` });
    } catch (e) {
      toast({
        title: 'Could not change role',
        description: e.response?.data?.detail || '',
        variant: 'destructive',
      });
    }
  };

  const setPostApproval = async (postId, status) => {
    try {
      await adminSetPostApproval(postId, status);
      setPending((ps) => ps.filter((p) => p.id !== postId));
      toast({ title: `Post ${status}` });
    } catch { toast({ title: 'Could not update post', variant: 'destructive' }); }
  };

  const updatePayout = async (payoutId, status) => {
    try { await adminUpdatePayout(payoutId, status); toast({ title: `Payout ${status}` }); await load(); }
    catch { toast({ title: 'Could not update payout', variant: 'destructive' }); }
  };

  const saveSettings = async (payload) => {
    // Phase 27.1.5 — return the backend response so per-section savers
    // can render their own applied/ignored summary. Toast is owned by
    // the caller (section-specific message + counts).
    const res = await adminUpdateSettings(payload);
    const sett = await adminGetSettings();
    setSettings(sett);
    return res;
  };

  return (
    <div className="mx-auto max-w-[1200px] px-4 pt-6 pb-24 md:pt-10">
      <div className="mb-6">
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <ShieldCheck className="h-6 w-6 gold-text" /> {t('page.title')}
        </h1>
        <p className="text-sm text-muted-foreground">{t('page.subtitle')}</p>
      </div>

      <div className="mb-6 flex flex-wrap gap-2 overflow-x-auto rounded-xl border border-border bg-card/50 p-2">
        <TabBtn testId="admin-tab-overview" active={tab === 'overview'} onClick={() => setTab('overview')} icon={DollarSign}>{t('page.tabs.overview')}</TabBtn>
        <TabBtn testId="admin-tab-users" active={tab === 'users'} onClick={() => setTab('users')} icon={Users}>{t('page.tabs.users')}</TabBtn>
        <TabBtn testId="admin-tab-team" active={tab === 'team'} onClick={() => setTab('team')} icon={ShieldCheck}>Team</TabBtn>
        <TabBtn testId="admin-tab-approvals" active={tab === 'approvals'} onClick={() => setTab('approvals')} icon={FileCheck2}>{t('page.tabs.approvals')} {pending.length > 0 && <span className="ml-1 rounded-full bg-amber-500/30 px-1.5 text-[10px]">{pending.length}</span>}</TabBtn>
        <TabBtn testId="admin-tab-finance" active={tab === 'finance'} onClick={() => setTab('finance')} icon={Wallet}>{t('page.tabs.finance')}</TabBtn>
        <TabBtn testId="admin-tab-moderation" active={tab === 'moderation'} onClick={() => setTab('moderation')} icon={Shield}>{t('page.tabs.moderation')}</TabBtn>
        <TabBtn testId="admin-tab-reports" active={tab === 'reports'} onClick={() => setTab('reports')} icon={Flag}>
          Segnalazioni
          {reportsNew > 0 && (
            <span
              className="ml-1 rounded-full bg-red-500 px-1.5 text-[10px] font-semibold text-white"
              data-testid="admin-reports-badge"
            >
              {reportsNew}
            </span>
          )}
        </TabBtn>
        <TabBtn testId="admin-tab-lives" active={tab === 'lives'} onClick={() => setTab('lives')} icon={Radio}>{t('page.tabs.lives')}</TabBtn>
        <TabBtn testId="admin-tab-payouts" active={tab === 'payouts'} onClick={() => setTab('payouts')} icon={ArrowDownToLine}>{t('page.tabs.payouts')}</TabBtn>
        <TabBtn testId="admin-tab-refunds" active={tab === 'refunds'} onClick={() => setTab('refunds')} icon={RefreshCcw}>{t('page.tabs.refunds')}</TabBtn>
        <TabBtn testId="admin-tab-disputes" active={tab === 'disputes'} onClick={() => setTab('disputes')} icon={Flag}>Dispute</TabBtn>
        <a
          href="/admin/o2o"
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-white/5"
          data-testid="admin-tab-o2o"
        >
          <ShieldCheck className="h-3.5 w-3.5" /> {t('page.tabs.o2o')}
        </a>
        <a
          href="/admin/wallet/packages"
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-white/5"
          data-testid="admin-tab-wallet-packages"
        >
          <Wallet className="h-3.5 w-3.5" /> Pacchetti
        </a>
        <TabBtn testId="admin-tab-emails" active={tab === 'emails'} onClick={() => setTab('emails')} icon={Mail}>{t('page.tabs.emails')}</TabBtn>
        <TabBtn testId="admin-tab-mediaperf" active={tab === 'mediaperf'} onClick={() => setTab('mediaperf')} icon={BarChart3}>Performance Media</TabBtn>
        <TabBtn testId="admin-tab-campaigns" active={tab === 'campaigns'} onClick={() => setTab('campaigns')} icon={Megaphone}>Campaign Links</TabBtn>
        <TabBtn testId="admin-tab-fiscal" active={tab === 'fiscal'} onClick={() => setTab('fiscal')} icon={Landmark}>Fiscale Voce Pro</TabBtn>
        <TabBtn testId="admin-tab-aperitivo" active={tab === 'aperitivo'} onClick={() => setTab('aperitivo')} icon={Martini}>Aperitivo Nymia</TabBtn>
        <TabBtn testId="admin-tab-categories" active={tab === 'categories'} onClick={() => setTab('categories')} icon={Tags}>Categorie Persone</TabBtn>
        {isSuperAdmin && (
          <TabBtn testId="admin-tab-apple-cleanup" active={tab === 'apple-cleanup'} onClick={() => setTab('apple-cleanup')} icon={ShieldAlert}>Pulizia Apple 1.1.4</TabBtn>
        )}
        <TabBtn testId="admin-tab-help" active={tab === 'help'} onClick={() => setTab('help')} icon={MessageCircle}>
          Assistenza
          {helpUnread > 0 && (
            <span
              className="ml-1 rounded-full bg-red-500 px-1.5 text-[10px] font-semibold text-white"
              data-testid="admin-help-badge"
            >
              {helpUnread}
            </span>
          )}
        </TabBtn>
        <TabBtn testId="admin-tab-settings" active={tab === 'settings'} onClick={() => setTab('settings')} icon={SettingsIcon}>{t('page.tabs.settings')}</TabBtn>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <div className="h-7 w-7 animate-spin rounded-full border-2 border-border border-t-amber-400" />
        </div>
      ) : (
        <>
          {tab === 'overview' && stats && (
            <div className="space-y-6">
              <OnlineNowCard />
              <AdminOverviewPanel />
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <StatBox icon={Users} label={t('page.stat_labels.users')} value={stats.total_users} color="bg-blue-500/20 text-blue-300" />
                <StatBox icon={FileVideo} label={t('page.stat_labels.posts_paid')} value={`${stats.total_posts} (${stats.paid_posts})`} color="bg-amber-500/20 gold-text" />
                <StatBox icon={DollarSign} label={t('page.stat_labels.gross_revenue')} value={fmtCredits(stats.gross_revenue)} color="bg-emerald-500/20 text-emerald-300" />
                <StatBox icon={BadgePercent} label={t('page.stat_labels.platform_revenue')} value={fmtCredits(stats.platform_revenue)} color="bg-violet-500/20 text-violet-300" />
              </div>
            </div>
          )}

          {tab === 'users' && (
            <div className="rounded-xl border border-border bg-card">
              <header className="flex items-center justify-between border-b border-border px-5 py-3">
                <h2 className="text-sm font-semibold">{t('page.sections.users_and_fees')}</h2>
                <span className="text-xs text-muted-foreground" data-testid="admin-users-counter">
                  {(userSearch || userVipFilter !== 'all')
                    ? `${visibleUsers.length} / ${users.length} utenti`
                    : t('page.sections.users_count', { count: users.length })}
                </span>
              </header>
              {/* Phase 85 — search + VIP filter toolbar */}
              <div className="flex flex-col gap-2 border-b border-border bg-background/30 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="relative w-full sm:max-w-sm">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Cerca per @username o email…"
                    className="h-9 bg-background pl-9 pr-8"
                    data-testid="admin-users-search-input"
                  />
                  {userSearch && (
                    <button
                      type="button"
                      onClick={() => setUserSearch('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label="Pulisci ricerca"
                      data-testid="admin-users-search-clear"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {[
                    { key: 'all', label: 'Tutti gli utenti' },
                    { key: 'vip', label: 'Solo Voce Pro', Icon: Crown },
                    { key: 'normal', label: 'Solo Utenti normali' },
                  ].map(({ key, label, Icon }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setUserVipFilter(key)}
                      className={`flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                        userVipFilter === key
                          ? 'gold-gradient border-transparent text-black'
                          : 'border-border bg-background/40 text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid={`admin-users-filter-${key}`}
                    >
                      {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-background/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="px-4 py-2 font-semibold">User</th>
                      <th className="px-4 py-2 font-semibold">Email</th>
                      <th className="px-4 py-2 text-center font-semibold">Posts</th>
                      <th className="px-4 py-2 text-right font-semibold">Wallet</th>
                      <th className="px-4 py-2 font-semibold">Fee %</th>
                      <th className="px-4 py-2 font-semibold">Status</th>
                      <th className="px-4 py-2 font-semibold">Account</th>
                      <th className="px-4 py-2 font-semibold">Voce Pro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleUsers.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="admin-users-empty">
                          Nessun utente corrisponde alla ricerca.
                        </td>
                      </tr>
                    )}
                    {visibleUsers.map((u) => (
                      <UserRow
                        key={u.id}
                        user={u}
                        onSaveFee={saveFee}
                        onSetStatus={setStatus}
                        onPause={pauseUser}
                        onResume={resumeUser}
                        onDelete={removeUser}
                        onSuspendMonet={suspendMonet}
                        onResumeMonet={resumeMonet}
                        onSetRole={setUserRole}
                        canManageRoles={isSuperAdmin}
                        onOpenBilling={setBillingDialogUserId}
                        onOpenWallet={setWalletDialogUser}
                        onStandby={standbyUser}
                        onReleaseStandby={releaseStandby}
                        onPreviewCard={setCardPreviewUser}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'team' && (
            <AdminTeamPanel />
          )}

          {tab === 'approvals' && (
            <div className="rounded-xl border border-border bg-card" data-testid="approvals-queue">
              <header className="flex items-center justify-between border-b border-border px-5 py-3">
                <div>
                  <h2 className="text-sm font-semibold">Coda di Moderazione</h2>
                  <p className="text-[11px] text-muted-foreground">
                    Contenuti in attesa di revisione manuale (segnalati dalla moderazione AI o post a pagamento da approvare).
                  </p>
                </div>
                <span className="text-xs text-muted-foreground" data-testid="approvals-count">{pending.length} in attesa</span>
              </header>
              {pending.length === 0 ? (
                <div className="px-5 py-12 text-center text-sm text-muted-foreground" data-testid="approvals-empty">
                  <FileCheck2 className="mx-auto mb-2 h-7 w-7 gold-text" />
                  Nessun contenuto in attesa di revisione.
                </div>
              ) : (
                <ul className="divide-y divide-border">
                  {pending.map((p) => (
                    <li key={p.id} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:gap-4" data-testid={`approval-row-${p.id}`}>
                      <button
                        onClick={() => setApprovalPreview(p.id)}
                        className="relative h-24 w-24 shrink-0 overflow-hidden rounded-lg border border-border bg-secondary transition-opacity hover:opacity-80"
                        title="Apri anteprima completa"
                        data-testid={`approval-preview-${p.id}`}
                      >
                        {p.media_type === 'image' && (
                          <img src={mediaUrl(p.media_url)} alt="" className="h-full w-full object-cover" />
                        )}
                        {p.media_type === 'video' && (
                          <video src={mediaUrl(p.media_url)} muted preload="metadata" className="h-full w-full object-cover" />
                        )}
                        {p.media_type === 'audio' && (
                          <div className="flex h-full w-full items-center justify-center text-[11px] uppercase text-muted-foreground">audio</div>
                        )}
                        <span className="absolute bottom-1 right-1 rounded bg-black/70 px-1 text-[9px] uppercase text-white">{p.media_type}</span>
                      </button>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold">@{p.username}</p>
                        <p className="line-clamp-2 text-xs text-muted-foreground">{p.caption || 'Nessuna didascalia'}</p>
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                          <span className="text-[11px] uppercase gold-text">{p.is_paid ? `${p.price} cr` : 'gratuito'}</span>
                          {p.moderation_reason && (
                            <span
                              className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold text-amber-300"
                              data-testid={`approval-ai-reason-${p.id}`}
                            >
                              <AlertTriangle className="h-3 w-3" /> AI: {p.moderation_reason}
                            </span>
                          )}
                          <span className="text-[10px] text-muted-foreground">{new Date(p.created_at).toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="flex shrink-0 gap-2">
                        <Button
                          onClick={() => setPostApproval(p.id, 'approved')}
                          size="sm"
                          className="h-9 bg-emerald-500 px-4 font-semibold text-white hover:bg-emerald-600"
                          data-testid={`approval-approve-${p.id}`}
                        >
                          <Check className="mr-1 h-3.5 w-3.5" /> Approva
                        </Button>
                        <Button
                          onClick={() => setPostApproval(p.id, 'rejected')}
                          size="sm"
                          variant="outline"
                          className="h-9 border-red-500/50 px-4 font-semibold text-red-400 hover:bg-red-500/10 hover:text-red-300"
                          data-testid={`approval-reject-${p.id}`}
                        >
                          <X className="mr-1 h-3.5 w-3.5" /> Rifiuta
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <PostPreviewModal
                open={!!approvalPreview}
                onOpenChange={(o) => !o && setApprovalPreview(null)}
                postId={approvalPreview}
              />
            </div>
          )}

          {tab === 'finance' && finance && (
            <div className="space-y-6">
              <AccountingOrdersPanel />

              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <StatBox icon={DollarSign} label="Gross revenue" value={`${Math.round(finance.gross_revenue).toLocaleString()} cr`} color="bg-emerald-500/20 text-emerald-300" />
                <StatBox icon={BadgePercent} label="Platform earnings" value={`${Math.round(finance.platform_revenue).toLocaleString()} cr`} color="bg-amber-500/20 gold-text" />
                <StatBox icon={Wallet} label="Held in wallets" value={`${Math.round(finance.wallet_held).toLocaleString()} cr`} color="bg-violet-500/20 text-violet-300" />
                <StatBox icon={ArrowDownToLine} label="Pending payouts" value={`${Math.round(finance.pending_payouts.amount).toLocaleString()} cr`} color="bg-blue-500/20 text-blue-300" />
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="rounded-xl border border-border bg-card p-4">
                  <p className="text-xs uppercase text-muted-foreground">Net paid to Voice Pros</p>
                  <p className="mt-1 text-xl font-semibold tabular-nums">{Math.round(finance.net_to_creators).toLocaleString()} cr</p>
                </div>
                <div className="rounded-xl border border-border bg-card p-4">
                  <p className="text-xs uppercase text-muted-foreground">Sales count</p>
                  <p className="mt-1 text-xl font-semibold tabular-nums">{finance.sales_count}</p>
                </div>
                <div className="rounded-xl border border-border bg-card p-4">
                  <p className="text-xs uppercase text-muted-foreground">Lives (active / scheduled)</p>
                  <p className="mt-1 text-xl font-semibold tabular-nums">{finance.active_lives} / {finance.scheduled_lives}</p>
                </div>
              </div>
            </div>
          )}

          {tab === 'lives' && (
            <div className="space-y-6">
            <div className="rounded-xl border border-border bg-card">
              <header className="flex items-center justify-between border-b border-border px-5 py-3">
                <h2 className="flex items-center gap-2 text-sm font-semibold"><Radio className="h-4 w-4 text-red-400" /> {t('page.sections.live_now')}</h2>
                <span className="text-xs text-muted-foreground">{activeLives.length} broadcasting</span>
              </header>
              {activeLives.length === 0 ? (
                <div className="px-5 py-12 text-center text-sm text-muted-foreground">No active lives right now.</div>
              ) : (
                <ul className="divide-y divide-border">
                  {activeLives.map((l) => (
                    <li key={l.id} className="flex items-center justify-between px-5 py-3">
                      <div>
                        <p className="text-sm font-semibold">{l.title}</p>
                        <p className="text-[11px] text-muted-foreground">@{l.creator_username} · {l.kind} · started {l.started_at ? new Date(l.started_at).toLocaleTimeString() : '?'}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs gold-text">{l.tickets_sold} tickets</span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase text-red-300">
                          <Users className="h-3 w-3" /> {l.viewers_count}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <AdminLiveRefundsPanel />
            </div>
          )}

          {tab === 'payouts' && (
            <div className="rounded-xl border border-border bg-card">
              <header className="flex items-center justify-between border-b border-border px-5 py-3">
                <h2 className="flex items-center gap-2 text-sm font-semibold"><ArrowDownToLine className="h-4 w-4" /> {t('page.sections.payout_requests')}</h2>
                <span className="text-xs text-muted-foreground">
                  Pending: {fmtCredits(stats?.pending_payouts || 0)}
                </span>
              </header>
              {payouts.length === 0 ? (
                <div className="py-10 text-center text-sm text-muted-foreground">No payout requests yet.</div>
              ) : (
                <ul className="divide-y divide-border">
                  {payouts.map((p) => (
                    <li key={p.id} className="flex items-center justify-between px-5 py-3">
                      <div>
                        <p className="text-sm font-semibold">@{p.username}</p>
                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{new Date(p.created_at).toLocaleString()}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold tabular-nums">{fmtCredits(p.amount)}</span>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                          p.status === 'completed' ? 'bg-emerald-500/20 text-emerald-300' :
                          p.status === 'rejected' ? 'bg-red-500/20 text-red-300' :
                          'bg-amber-500/20 gold-text'
                        }`}>{p.status}</span>
                        {p.status === 'pending' && (
                          <div className="flex gap-1">
                            <Button size="sm" onClick={() => updatePayout(p.id, 'completed')} className="h-7 bg-emerald-500 px-2 text-xs hover:bg-emerald-600">Approve</Button>
                            <Button size="sm" variant="ghost" onClick={() => updatePayout(p.id, 'rejected')} className="h-7 px-2 text-xs text-red-400 hover:bg-red-500/10">Reject</Button>
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {tab === 'settings' && (
            <div>
              {!isSuperAdmin ? (
                <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-6 text-center" data-testid="settings-locked-panel">
                  <ShieldCheck className="mx-auto mb-2 h-8 w-8 text-red-300" />
                  <p className="text-base font-semibold text-foreground">Super Admin only</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Integration keys (Stripe, LiveKit, Wasabi, SMTP, Meta, Sightengine) and Tip presets are reserved
                    to the Super Admin role. Ask your Super Admin to grant access if you need to edit them.
                  </p>
                </div>
              ) : settings ? (
                <>
                  <div
                    className="mb-4 flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/20 p-3 text-xs text-foreground"
                    data-testid="settings-encryption-notice"
                  >
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 gold-text" />
                    <div>
                      Real keys are stored encrypted in the database and used only when <strong>Mock mode</strong> is OFF for the relevant integration.
                      Sensitive values are never returned in plaintext — leave a secret field blank to keep its existing value.
                    </div>
                  </div>
                  {/* TipPresetsEditor spostato in /admin/wallet/packages (Jul 2026). */}
                  <SettingsSection data={settings} onSave={saveSettings} />
                  <MaintenancePanel />
                </>
              ) : null}
            </div>
          )}
          {tab === 'emails' && (
            <EmailManagerTab />
          )}
          {tab === 'refunds' && (
            <RefundsTab />
          )}
          {tab === 'disputes' && (
            <AdminDisputesPanel />
          )}
          {tab === 'moderation' && (
            <ContentModerationPanel />
          )}
          {tab === 'reports' && (
            <AdminReportsPanel />
          )}
          {tab === 'help' && (
            <div className="space-y-6">
              <AdminHelpCenterPanel />
              <AdminWhatsAppPanel />
            </div>
          )}
          {tab === 'mediaperf' && (
            <AdminMediaPerformancePanel />
          )}
          {tab === 'aperitivo' && (
            <AdminAperitivoPanel />
          )}

          {tab === 'categories' && (
            <div className="max-w-2xl space-y-3">
              <h2 className="text-lg font-bold">Categorie Persone</h2>
              <AdminCategoriesPanel />
            </div>
          )}

          {tab === 'apple-cleanup' && isSuperAdmin && (
            <AdminAppleCleanupPanel />
          )}

          {tab === 'campaigns' && (
            <AdminCampaignLinksPanel />
          )}
          {tab === 'fiscal' && (
            <AdminCreatorFiscalPanel onOpenBilling={setBillingDialogUserId} />
          )}
        </>
      )}
      {/* Phase 42 — Creator Stripe/fiscal profile dialog */}
      <CreatorBillingProfileDialog
        open={!!billingDialogUserId}
        userId={billingDialogUserId}
        onOpenChange={(o) => { if (!o) setBillingDialogUserId(null); }}
      />
      {/* Jul 2026 — Admin manual wallet adjustment */}
      <AdminUserCardPreviewDialog
        open={!!cardPreviewUser}
        user={cardPreviewUser}
        onClose={() => setCardPreviewUser(null)}
      />
      <AdminWalletAdjustDialog
        open={!!walletDialogUser}
        user={walletDialogUser}
        onOpenChange={(o) => { if (!o) setWalletDialogUser(null); }}
        onDone={() => load && load()}
      />
    </div>
  );
};

export default AdminPage;
